import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";

// API Service for Payments
const paymentApiService = {
  // Search users - fetches all then filters locally since API doesn't support search
  searchUsers: async () => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user orders
  getUserOrders: async (userId) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/order/`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      const allOrders = await response.json();
      return allOrders.filter(order => order.user_id == userId)
        .sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Sort by date ascending (oldest first)
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // Get user's transaction history
  getUserTransactions: async (qbUserId) => {
    try {
      const response = await fetch("https://qwikbistro.shop/transaction/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch transactions");

      const data = await response.json();
      // Filter transactions for this specific user
      const userTransactions = data.filter(t => t.qb_user_id == qbUserId)
        .sort((a, b) => new Date(b.date || b.systemdate) - new Date(a.date || a.systemdate)); // Latest first

      return userTransactions;
    } catch (err) {
      console.error("Error fetching transactions:", err);
      return [];
    }
  },

  // Get user's last balance from their transaction history
  getUserLastBalance: async (qbUserId) => {
    try {
      const userTransactions =
        await paymentApiService.getUserTransactions(qbUserId);

      console.log("user transaction", userTransactions);

      if (!userTransactions || userTransactions.length === 0) {
        return 0;
      }

      // Sort by latest date first
      const sortedTransactions = [...userTransactions].sort(
        (a, b) =>
          new Date(b.systemdate || b.date) -
          new Date(a.systemdate || a.date)
      );

      // Get latest transaction with valid balance
      const latestTransaction = sortedTransactions.find(
        (t) =>
          t.balance !== null &&
          t.balance !== undefined &&
          t.balance !== ""
      );

      return latestTransaction
        ? parseFloat(latestTransaction.balance) || 0
        : 0;
    } catch (err) {
      console.error("Error fetching user balance:", err);
      return 0;
    }
  },

  // Create new transaction
  createTransaction: async (transactionData) => {
    try {
      const response = await fetch("https://qwikbistro.shop/transaction/new", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
        body: JSON.stringify(transactionData),
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },

  // Update order payment status and paid amount
  updateOrderPayment: async (orderId, updateData) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/order/${orderId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
        body: JSON.stringify(updateData),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },
};

const CreatePaymentPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userBalance, setUserBalance] = useState(0);

  console.log("lst balanceeeeee", userBalance);
  const [userTransactions, setUserTransactions] = useState([]);
  const [paymentAllocation, setPaymentAllocation] = useState([]);
  const [useAvailableBalance, setUseAvailableBalance] = useState(false);
  const [amountFromBalance, setAmountFromBalance] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    customer: null,
    // New fields
    paymentMode: "cash", // cash, etransfer, creditcard
    amount: "", // Total amount received from customer (cash/payment)
    includesTips: "no", // yes or no
    tips: "", // Tips amount (only if includesTips is "yes")
    totalPaid: "", // Calculated total payment (amount + balance used)
    availableForOrders: "", // Amount available for order payments

    receivedby: ""
  });

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setUserLoading(true);
      try {
        const allUsers = await paymentApiService.searchUsers();
        setUsers(allUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users");
      } finally {
        setUserLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search term (client-side filtering)
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users.slice(0, 20); // Return first 20 users if no search

    const searchLower = searchTerm.toLowerCase();
    return users.filter(user =>
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.qb_user_id && user.qb_user_id.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.includes(searchTerm)) ||
      (user.email && user.email.toLowerCase().includes(searchLower))
    ).slice(0, 20); // Limit to 20 results
  }, [users, searchTerm]);

  // Fetch user data when selected
  useEffect(() => {
    const fetchUserData = async () => {
      if (!selectedUser) {
        setOrders([]);
        setUnpaidOrders([]);
        setUserBalance(0);
        setUserTransactions([]);
        setPaymentAllocation([]);
        setUseAvailableBalance(false);
        setAmountFromBalance(0);
        return;
      }

      try {
        // Fetch user's last balance
        const balance = await paymentApiService.getUserLastBalance(selectedUser.qb_user_id);
        setUserBalance(balance);

        // Fetch user's transaction history
        const transactions = await paymentApiService.getUserTransactions(selectedUser.qb_user_id);
        setUserTransactions(transactions);

        // Fetch user's orders
        const userOrders = await paymentApiService.getUserOrders(selectedUser.id);
        setOrders(userOrders);

        // Filter unpaid or partially paid orders
        const unpaid = userOrders.filter(order =>
          order.payment_status === "not paid" ||
          (parseFloat(order.paidamount || 0) < parseFloat(order.total_amount || 0))
        ).sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Oldest first

        setUnpaidOrders(unpaid);

        // Initialize payment allocation
        const allocation = unpaid.map(order => ({
          orderId: order.id,
          qbOrderId: order.qb_order_id,
          orderDate: order.order_date,
          totalAmount: parseFloat(order.total_amount || 0),
          alreadyPaid: parseFloat(order.paidamount || 0),
          remaining: parseFloat(order.total_amount || 0) - parseFloat(order.paidamount || 0),
          allocated: 0,
          newPaidAmount: parseFloat(order.paidamount || 0),
          newStatus: order.payment_status,
          originalStatus: order.payment_status
        }));

        setPaymentAllocation(allocation);

      } catch (err) {
        console.error("Error fetching user data:", err);
        toast.error("Failed to load customer data");
      }
    };

    fetchUserData();
  }, [selectedUser]);

  // Calculate available balance that can be used (only if balance is negative/credit)
  const availableCredit = useMemo(() => {
    if (userBalance < 0) {
      return Math.abs(userBalance); // Convert negative to positive (credit available)
    }
    return 0;
  }, [userBalance]);

  // Calculate total unpaid amount
  const calculateTotalUnpaid = () => {
    return unpaidOrders.reduce((total, order) => {
      const orderTotal = parseFloat(order.total_amount || 0);
      const alreadyPaid = parseFloat(order.paidamount || 0);
      return total + (orderTotal - alreadyPaid);
    }, 0);
  };

  // Calculate total amount needed (positive balance + unpaid orders)
  const calculateTotalAmountNeeded = () => {
    let totalNeeded = 0;

    // If user has positive balance (owes money), add it
    if (userBalance > 0) {
      totalNeeded += userBalance;
    }

    // Add unpaid orders
    totalNeeded += calculateTotalUnpaid();

    return totalNeeded;
  };

  // Calculate payment amounts when amount or tips change
  useEffect(() => {
    const cashAmount = parseFloat(formData.amount) || 0;
    const tips = parseFloat(formData.tips) || 0;
    const balanceToUse = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;

    if (formData.includesTips === "yes") {
      // If includes tips: total payment = (cash amount - tips) + balance used
      const cashForOrders = cashAmount - tips;
      const totalForOrders = cashForOrders + balanceToUse;

      setFormData(prev => ({
        ...prev,
        totalPaid: cashAmount > 0 ? cashAmount.toFixed(2) : "0.00",
        availableForOrders: totalForOrders > 0 ? totalForOrders.toFixed(2) : "0.00"
      }));
    } else {
      // If no tips: total payment = cash amount + balance used
      const totalForOrders = cashAmount + balanceToUse;

      setFormData(prev => ({
        ...prev,
        totalPaid: cashAmount > 0 ? cashAmount.toFixed(2) : "0.00",
        availableForOrders: totalForOrders > 0 ? totalForOrders.toFixed(2) : "0.00"
      }));
    }
  }, [formData.amount, formData.tips, formData.includesTips, useAvailableBalance, amountFromBalance, availableCredit]);

  // Calculate payment distribution when available amount changes
  useEffect(() => {
    const available = parseFloat(formData.availableForOrders) || 0;
    if (available <= 0 || paymentAllocation.length === 0) {
      // Reset allocation if no available amount
      setPaymentAllocation(prev =>
        prev.map(order => ({
          ...order,
          allocated: 0,
          newPaidAmount: order.alreadyPaid,
          newStatus: order.originalStatus
        }))
      );
      return;
    }

    let remainingAmount = available;
    const updatedAllocation = [...paymentAllocation];

    // Distribute payment across orders (oldest first)
    for (let i = 0; i < updatedAllocation.length; i++) {
      const order = updatedAllocation[i];

      if (remainingAmount <= 0) break;

      if (order.remaining > 0) {
        const allocateAmount = Math.min(order.remaining, remainingAmount);
        order.allocated = allocateAmount;
        order.newPaidAmount = order.alreadyPaid + allocateAmount;

        // Check if order is now fully paid
        if (order.newPaidAmount >= order.totalAmount - 0.01) { // Allow for rounding errors
          order.newPaidAmount = order.totalAmount;
          order.newStatus = "paid";
        } else {
          order.newStatus = "not paid";
        }

        remainingAmount -= allocateAmount;
      }
    }

    setPaymentAllocation(updatedAllocation);
  }, [formData.availableForOrders, paymentAllocation.length]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // If amount changes and includesTips is "no", clear tips
    if (field === "amount" && formData.includesTips === "no") {
      setFormData(prev => ({ ...prev, tips: "" }));
    }

    // If switching from "yes" to "no" for includesTips, clear tips
    if (field === "includesTips" && value === "no") {
      setFormData(prev => ({ ...prev, tips: "" }));
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({ ...prev, customer: user }));
    setSelectedOrder(null);
    setSearchTerm('');
  };

  // Handle use available balance change
  const handleUseBalanceChange = (e) => {
    const useBalance = e.target.value === "yes";
    setUseAvailableBalance(useBalance);

    if (!useBalance) {
      setAmountFromBalance(0);
    } else {
      // Set max amount to available credit
      setAmountFromBalance(Math.min(availableCredit, calculateTotalUnpaid()));
    }
  };

  // Calculate total allocated amount
  const calculateTotalAllocated = () => {
    return paymentAllocation.reduce((total, order) => total + order.allocated, 0);
  };

  // Calculate total amount paid (cash + credit used)
  const calculateTotalPayment = () => {
    const cashAmount = parseFloat(formData.amount) || 0;
    const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;
    const tips = parseFloat(formData.tips) || 0;

    // Total payment is cash minus tips plus credit used
    return cashAmount + balanceUsed - tips;
  };

  // Calculate new user balance after payment - FIXED LOGIC
  const calculateNewUserBalance = () => {
    const totalPayment = calculateTotalPayment();
    const totalAllocated = calculateTotalAllocated();
    const remainingAfterAllocation = parseFloat(formData.availableForOrders || 0) - totalAllocated;

    // Start with current balance
    let newBalance = userBalance;

    // Apply total payment (reduces positive balance or creates negative balance)
    newBalance -= totalPayment;

    return newBalance;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customer) {
      toast.error("Please select a customer");
      return;
    }

    // Check if user is making a payment
    const cashAmount = parseFloat(formData.amount) || 0;
    const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;

    if (cashAmount <= 0 && balanceUsed <= 0) {
      toast.error("Please enter payment amount or use available balance");
      return;
    }

    if (formData.includesTips === "yes" && (!formData.tips || parseFloat(formData.tips) <= 0)) {
      toast.error("Please enter tips amount");
      return;
    }

    if (formData.includesTips === "yes" && parseFloat(formData.tips) >= cashAmount) {
      toast.error("Tips cannot be greater than or equal to the cash amount");
      return;
    }

    const totalAmountForOrders = parseFloat(formData.availableForOrders) || 0;

    if (totalAmountForOrders <= 0) {
      toast.error("No amount available for order payments");
      return;
    }

    setLoading(true);
    try {
      // First, update all affected orders
      const orderUpdates = [];
      for (const allocation of paymentAllocation) {
        if (allocation.allocated > 0) {
          const updateData = {
            paidamount: allocation.newPaidAmount.toFixed(2),
            payment_status: allocation.newStatus,
            paymentdate: formData.date
          };

          try {
            const result = await paymentApiService.updateOrderPayment(allocation.orderId, updateData);
            orderUpdates.push({
              orderId: allocation.orderId,
              qbOrderId: allocation.qbOrderId,
              success: true,
              data: result
            });
          } catch (error) {
            orderUpdates.push({
              orderId: allocation.orderId,
              qbOrderId: allocation.qbOrderId,
              success: false,
              error: error.message
            });
          }
        }
      }

      // Check if any order updates failed
      const failedUpdates = orderUpdates.filter(update => !update.success);
      if (failedUpdates.length > 0) {
        console.error("Failed order updates:", failedUpdates);
        toast.error(`Failed to update ${failedUpdates.length} order(s). Please check the orders manually.`);
      }

      // Calculate new user balance
      const newUserBalance = calculateNewUserBalance();

      // Then, create the transaction record
      const cashAmount = parseFloat(formData.amount) || 0;
      const tipsAmount = parseFloat(formData.tips) || 0;
      const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;
      const totalAllocated = calculateTotalAllocated();

      const transactionPayload = {
        qb_user_id: formData.customer.qb_user_id,
        system_user_id: formData.customer.id.toString(),
        date: formData.date,
        qb_order_id: orderUpdates.length > 0 ? orderUpdates[0].qbOrderId : "",
        system_order_id: orderUpdates.length > 0 ? orderUpdates[0].orderId.toString() : "",
        total: cashAmount.toFixed(2),
        paid: cashAmount.toFixed(2),
        tips: tipsAmount.toFixed(2),
        balance: newUserBalance.toFixed(2),
        payment_mode: formData.paymentMode,
        allocated_to_orders: totalAllocated.toFixed(2),
        balance_used: balanceUsed.toFixed(2),
        remaining_credit: Math.max(0, parseFloat(formData.availableForOrders || 0) - totalAllocated).toFixed(2),

        receivedby: formData.receivedby
      };


      console.log("transactionPayload", transactionPayload);

      const transactionResult = await paymentApiService.createTransaction(transactionPayload);

      // Show success summary
      const paidOrders = paymentAllocation.filter(order => order.newStatus === "paid" && order.originalStatus !== "paid");
      const partiallyPaid = paymentAllocation.filter(order => order.newStatus === "not paid" && order.allocated > 0);

      let successMessage = `Payment processed successfully. `;

      if (cashAmount > 0) {
        successMessage += `Cash: $${cashAmount.toFixed(2)}. `;
      }

      if (balanceUsed > 0) {
        successMessage += `Credit used: $${balanceUsed.toFixed(2)}. `;
      }

      if (paidOrders.length > 0) {
        successMessage += `Fully paid ${paidOrders.length} order(s): ${paidOrders.map(o => o.qbOrderId).join(', ')}. `;
      }

      if (partiallyPaid.length > 0) {
        successMessage += `Partially paid ${partiallyPaid.length} order(s). `;
      }

      successMessage += `New balance: $${newUserBalance.toFixed(2)}`;

      if (newUserBalance < 0) {
        successMessage += ` (Credit: $${Math.abs(newUserBalance).toFixed(2)})`;
      }

      toast.success(successMessage);

      // Reset form
      setFormData({
        date: new Date().toISOString().split("T")[0],
        customer: null,
        paymentMode: "cash",
        amount: "",
        includesTips: "no",
        tips: "",
        totalPaid: "",
        availableForOrders: "",
      });
      setSelectedUser(null);
      setSelectedOrder(null);
      setSearchTerm("");
      setOrders([]);
      setUnpaidOrders([]);
      setUserBalance(0);
      setUserTransactions([]);
      setPaymentAllocation([]);
      setUseAvailableBalance(false);
      setAmountFromBalance(0);

    } catch (error) {
      console.error("Error submitting payment:", error);
      toast.error("Failed to submit payment");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = parseFloat(amount || 0);
    return `$${Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getBalanceType = (balance) => {
    if (balance < 0) return "credit";
    if (balance > 0) return "owed";
    return "zero";
  };

  // Calculate remaining amount after allocation
  const calculateRemainingAfterAllocation = () => {
    return Math.max(0, parseFloat(formData.availableForOrders || 0) - calculateTotalAllocated());
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Payments" breadcrumbItem="Payment" />

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <h4 className="card-title mb-5 text-center fs-4">Individual Customer Payment Processing</h4> */}

                <form onSubmit={handleSubmit}>
                  {/* Payment Information */}
                  <div className="row mb-4">

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Payment From <span className="text-danger">*</span></label>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            value={selectedUser ? `${selectedUser.name} (${selectedUser.qb_user_id})` : searchTerm}
                            onChange={(e) => {
                              if (!selectedUser) {
                                setSearchTerm(e.target.value);
                              }
                            }}
                            onFocus={() => {
                              // Clear selection when clicking on input
                              if (selectedUser) {
                                setSelectedUser(null);
                                setFormData(prev => ({ ...prev, customer: null }));
                                setSearchTerm('');
                              }
                            }}
                            placeholder={selectedUser ? "Selected customer" : "Search by name, ID, phone, or email..."}
                          />

                          {/* Clear selection button when user is selected */}
                          {selectedUser && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y me-3"
                              onClick={() => {
                                setSelectedUser(null);
                                setFormData(prev => ({ ...prev, customer: null }));
                                setSearchTerm('');
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}

                          {/* User search results dropdown */}
                          {searchTerm.length >= 1 && !selectedUser && filteredUsers.length > 0 && (
                            <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-3"
                              style={{ maxHeight: '250px', overflowY: 'auto' }}>
                              {filteredUsers.map(user => (
                                <div
                                  key={user.id}
                                  className="p-2 border-bottom hover-bg-light cursor-pointer"
                                  onClick={() => {
                                    handleUserSelect(user);
                                    setSearchTerm('');
                                  }}
                                >
                                  <div className="d-flex justify-content-between">
                                    <span className="fw-medium">{user.name}</span>
                                    <small className="text-muted">{user.qb_user_id}</small>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {userLoading && (
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>


                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Show customer's current balance */}
                  {selectedUser && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className={`card ${getBalanceType(userBalance) === 'credit' ? 'bg-success bg-opacity-10' : getBalanceType(userBalance) === 'owed' ? 'bg-warning bg-opacity-10' : 'bg-light'}`}>
                          <div className="card-body">
                            <h6 className="card-title">
                              <i className={`fas ${getBalanceType(userBalance) === 'credit' ? 'fa-plus-circle text-success' : getBalanceType(userBalance) === 'owed' ? 'fa-exclamation-circle text-warning' : 'fa-check-circle text-dark'} me-2`}></i>
                              Customer Balance
                            </h6>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <p className="mb-0 fs-4">
                                  {userBalance < 0 ? (
                                    <span className="text-success fw-bold">
                                      Credit: {formatCurrency(userBalance)}
                                    </span>
                                  ) : userBalance > 0 ? (
                                    <span className="text-warning fw-bold">
                                      Owes: {formatCurrency(userBalance)}
                                    </span>
                                  ) : (
                                    <span className="text-dark">
                                      Balanced: {formatCurrency(userBalance)}
                                    </span>
                                  )}
                                </p>
                                <small className="text-muted">
                                  {userBalance < 0
                                    ? "Customer has credit balance (overpaid)"
                                    : userBalance > 0
                                      ? "Customer owes this amount"
                                      : "Customer balance is zero"}
                                </small>
                              </div>
                              {availableCredit > 0 && (
                                <div className="text-end">
                                  <small className="text-success d-block">
                                    <i className="fas fa-coins me-1"></i>
                                    Available Credit: {formatCurrency(availableCredit)}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show customer's unpaid orders summary */}
                  {/* {selectedUser && unpaidOrders.length > 0 && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="card bg-warning bg-opacity-10">
                          <div className="card-body">
                            <h6 className="card-title">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              Customer has {unpaidOrders.length} unpaid/partially paid order(s)
                            </h6>
                            <p className="mb-0">
                              Total unpaid amount: <strong>{formatCurrency(calculateTotalUnpaid())}</strong>
                            </p>
                            <small className="text-muted">Payments will be automatically allocated to the oldest unpaid orders first.</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )} */}



                  {/* Use Available Balance */}
                  {/* {selectedUser && availableCredit > 0 && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">Use Available Credit? <span className="text-danger">*</span></label>
                          <div className="d-flex gap-4 mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="useBalance"
                                id="useBalanceYes"
                                value="yes"
                                checked={useAvailableBalance === true}
                                onChange={handleUseBalanceChange}
                                required
                              />
                              <label className="form-check-label" htmlFor="useBalanceYes">
                                Yes
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="useBalance"
                                id="useBalanceNo"
                                value="no"
                                checked={useAvailableBalance === false}
                                onChange={handleUseBalanceChange}
                              />
                              <label className="form-check-label" htmlFor="useBalanceNo">
                                No
                              </label>
                            </div>
                          </div>

                          {useAvailableBalance && (
                            <div className="mt-3">
                              <label className="form-label">Amount from Credit <span className="text-danger">*</span></label>
                              <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={amountFromBalance}
                                  onChange={(e) => setAmountFromBalance(Math.min(parseFloat(e.target.value) || 0, availableCredit))}
                                  step="0.01"
                                  min="0.01"
                                  max={availableCredit}
                                  placeholder="0.00"
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => setAmountFromBalance(Math.min(availableCredit, calculateTotalUnpaid()))}
                                >
                                  Max
                                </button>
                              </div>
                              <small className="text-muted">
                                Available credit: {formatCurrency(availableCredit)}. Max recommended: {formatCurrency(Math.min(availableCredit, calculateTotalUnpaid()))}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )} */}

                  {/* Payment Mode */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Payment Mode <span className="text-danger">*</span></label>
                        <div className="d-flex gap-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMode"
                              id="cash"
                              value="cash"
                              checked={formData.paymentMode === "cash"}
                              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                              required
                            />
                            <label className="form-check-label" htmlFor="cash">
                              Cash
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMode"
                              id="etransfer"
                              value="etransfer"
                              checked={formData.paymentMode === "etransfer"}
                              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="etransfer">
                              E-transfer
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMode"
                              id="creditcard"
                              value="creditcard"
                              checked={formData.paymentMode === "creditcard"}
                              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="creditcard">
                              Credit/Debit Card
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cash Amount Field */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Cash/Payment Amount <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.amount}
                            onChange={(e) => handleInputChange('amount', e.target.value)}
                            required
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                          />
                        </div>
                        <small className="text-muted">
                          Enter the cash/payment amount received from customer {useAvailableBalance && '(in addition to credit used)'}
                        </small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Received By <span className="text-danger"></span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.receivedby}
                          onChange={(e) => handleInputChange('receivedby', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Includes Tips */}
                  <div className="row mb-4 align-items-center">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Includes Tips? <span className="text-danger">*</span></label>
                        <div className="d-flex gap-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="includesTips"
                              id="tipsYes"
                              value="yes"
                              checked={formData.includesTips === "yes"}
                              onChange={(e) => handleInputChange('includesTips', e.target.value)}
                              required
                            />
                            <label className="form-check-label" htmlFor="tipsYes">
                              Yes
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="includesTips"
                              id="tipsNo"
                              value="no"
                              checked={formData.includesTips === "no"}
                              onChange={(e) => handleInputChange('includesTips', e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="tipsNo">
                              No
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {formData.includesTips === "yes" && (
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Tips Amount <span className="text-danger">*</span></label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.tips}
                              onChange={(e) => handleInputChange('tips', e.target.value)}
                              required
                              step="0.01"
                              min="0.01"
                              max={formData.amount}
                              placeholder="0.00"
                            />
                          </div>
                          <small className="text-muted">Enter tips amount (will be deducted from cash for order payments)</small>
                        </div>
                      </div>

                    )}

                  </div>

                  {/* Tips Field (only shown if includesTips is "yes") */}


                  {/* Calculated Payment Amount */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title mb-3">Payment Summary</h6>

                          <div className="row">
                            <div className="col-md-12">
                              <div className="mb-2">
                                <div className="d-flex justify-content-between">
                                  <span>Cash/Payment Amount:</span>
                                  <span className="fw-semibold">{formatCurrency(formData.amount)}</span>
                                </div>
                              </div>
                            </div>
                            {/* <div className="col-md-6">
                              <div className="mb-2">
                                <div className="d-flex justify-content-between">
                                  <span>Payment Mode:</span>
                                  <span className="fw-semibold text-capitalize">
                                    {formData.paymentMode === "cash" ? "Cash" :
                                      formData.paymentMode === "etransfer" ? "E-transfer" :
                                        "Credit/Debit Card"}
                                  </span>
                                </div>
                              </div>
                            </div> */}
                          </div>

                          {useAvailableBalance && amountFromBalance > 0 && (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between">
                                    <span>Credit Used:</span>
                                    <span className="fw-semibold text-success">+{formatCurrency(amountFromBalance)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {formData.includesTips === "yes" && (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between">
                                    <span>Tips Deducted:</span>
                                    <span className="fw-semibold text-warning">-{formatCurrency(formData.tips)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="row">
                            <div className="col-md-12">
                              <div className="mb-2">
                                <div className="d-flex justify-content-between">
                                  <span>Total Available for Order Payments:</span>
                                  <span className="fw-bold fs-5 text-primary">
                                    {formatCurrency(formData.availableForOrders)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* <div className="row mt-3">
                            <div className="col-md-12">
                              <div className="alert alert-info mb-0">
                                <small>
                                  <strong>Calculation:</strong>
                                  {formData.includesTips === "yes"
                                    ? ` $${formData.amount || "0.00"} (Cash)`
                                    : ` $${formData.amount || "0.00"} (Cash)`}
                                  {useAvailableBalance && amountFromBalance > 0 && ` + $${amountFromBalance.toFixed(2)} (Credit)`}
                                  {formData.includesTips === "yes" && ` - $${formData.tips || "0.00"} (Tips)`}
                                  {` = $${formData.availableForOrders || "0.00"} (For Orders)`}
                                </small>
                              </div>
                            </div>
                          </div> */}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Allocation Preview */}
                  {/* {selectedUser && paymentAllocation.length > 0 && parseFloat(formData.availableForOrders) > 0 && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="card">
                          <div className="card-header bg-primary text-white">
                            <h6 className="mb-0">
                              <i className="fas fa-exchange-alt me-2"></i>
                              Payment Allocation (Oldest Orders First)
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="table-responsive">
                              <table className="table table-hover table-sm">
                                <thead>
                                  <tr>
                                    <th>Order #</th>
                                    <th>Date</th>
                                    <th>Total Amount</th>
                                    <th>Already Paid</th>
                                    <th>Remaining</th>
                                    <th>This Payment</th>
                                    <th>New Paid</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paymentAllocation.map((order, index) => (
                                    <tr key={order.orderId} className={order.allocated > 0 ? 'table-success' : ''}>
                                      <td>{order.qbOrderId}</td>
                                      <td>{formatDate(order.orderDate)}</td>
                                      <td>{formatCurrency(order.totalAmount)}</td>
                                      <td>{formatCurrency(order.alreadyPaid)}</td>
                                      <td>{formatCurrency(order.remaining)}</td>
                                      <td>
                                        <span className="fw-bold">{formatCurrency(order.allocated)}</span>
                                        {order.allocated > 0 && (
                                          <div className="progress mt-1" style={{ height: '5px' }}>
                                            <div
                                              className="progress-bar bg-success"
                                              style={{
                                                width: `${(order.allocated / order.totalAmount) * 100}%`
                                              }}
                                            ></div>
                                          </div>
                                        )}
                                      </td>
                                      <td>{formatCurrency(order.newPaidAmount)}</td>
                                      <td>
                                        <span className={`badge ${order.newStatus === 'paid' ? 'bg-success' : 'bg-warning'}`}>
                                          {order.newStatus === 'paid' ? 'PAID' : 'NOT PAID'}
                                          {order.originalStatus !== order.newStatus && ' *'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="table-secondary">
                                    <td colSpan="5" className="text-end fw-bold">Total Allocated:</td>
                                    <td className="fw-bold">{formatCurrency(calculateTotalAllocated())}</td>
                                    <td colSpan="2">
                                      {calculateRemainingAfterAllocation() > 0 ? (
                                        <span className="text-info">
                                          Remaining: {formatCurrency(calculateRemainingAfterAllocation())}
                                          <br />
                                          <small>Will be added to customer credit</small>
                                        </span>
                                      ) : (
                                        <span className="text-success">All allocated</span>
                                      )}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>

                            <div className="alert alert-info mt-3">
                              <small>
                                <i className="fas fa-info-circle me-1"></i>
                                <strong>How it works:</strong> Payment is automatically allocated to the oldest unpaid/partially paid orders first.
                                Orders marked with * will have their status changed. Remaining amount becomes customer credit for future payments.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )} */}

                  {/* New Balance Preview */}
                  {/* <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title">Balance Summary</h6>
                          <div className="d-flex justify-content-between">
                            <span>Current Balance:</span>
                            <span className={`fw-bold ${userBalance < 0 ? 'text-success' : userBalance > 0 ? 'text-warning' : ''}`}>
                              {userBalance < 0 ? `-${formatCurrency(userBalance)}` : formatCurrency(userBalance)}
                              {userBalance < 0 && ' (Credit)'}
                              {userBalance > 0 && ' (Owes)'}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between">
                            <span>Total Payment:</span>
                            <span className="fw-bold text-success">-{formatCurrency(calculateTotalPayment())}</span>
                          </div>

                          <hr />

                          <div className="d-flex justify-content-between">
                            <span className="fw-bold">New Balance:</span>
                            <span className={`fw-bold fs-5 ${calculateNewUserBalance() < 0 ? 'text-success' : calculateNewUserBalance() > 0 ? 'text-warning' : ''}`}>
                              {calculateNewUserBalance() < 0 ? `-${formatCurrency(calculateNewUserBalance())}` : formatCurrency(calculateNewUserBalance())}
                              {calculateNewUserBalance() < 0 && ' (Credit)'}
                              {calculateNewUserBalance() > 0 && ' (Owes)'}
                            </span>
                          </div>

                          <div className="alert alert-info mt-3 mb-0">
                            <small>
                              <strong>Balance Logic:</strong>
                              {userBalance < 0 && ` Customer has ${formatCurrency(Math.abs(userBalance))} credit.`}
                              {userBalance > 0 && ` Customer owes ${formatCurrency(userBalance)}.`}
                              {userBalance === 0 && ` Customer balance is zero.`}
                              {' Payments reduce owed amount or create credit if overpaid.'}
                              <br />
                              {calculateRemainingAfterAllocation() > 0 &&
                                ` $${calculateRemainingAfterAllocation().toFixed(2)} will be added as credit for future payments.`}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Form Actions */}
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex justify-content-between">
                        <div className="d-flex gap-2">
                          <button
                            type="submit"
                            className="btn btn-success px-4"
                            disabled={loading || !formData.customer || (!formData.amount && !useAvailableBalance)}
                          >
                            {loading ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-1"></i>
                                Processing Payment...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check-circle me-1"></i>
                                Process Payment
                              </>
                            )}
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setFormData({
                                date: new Date().toISOString().split("T")[0],
                                customer: null,
                                paymentMode: "cash",
                                amount: "",
                                includesTips: "no",
                                tips: "",
                                totalPaid: "",
                                availableForOrders: "",
                              });
                              setSelectedUser(null);
                              setSelectedOrder(null);
                              setSearchTerm("");
                              setOrders([]);
                              setUnpaidOrders([]);
                              setUserBalance(0);
                              setUserTransactions([]);
                              setPaymentAllocation([]);
                              setUseAvailableBalance(false);
                              setAmountFromBalance(0);
                            }}
                            disabled={loading}
                          >
                            <i className="fas fa-redo me-1"></i>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentPage;



// import React, { useState, useEffect, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import { toast } from "sonner";
// import Breadcrumb from "components/Common/Breadcrumb";
// import DashboardLoader from "components/Loader/DashboardLoader";

// // API Service for Payments
// const paymentApiService = {
//   // Search users - fetches all then filters locally since API doesn't support search
//   searchUsers: async () => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/userProfile/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       if (!response.ok) {
//         throw new Error("Failed to fetch users");
//       }
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching users:', error);
//       throw error;
//     }
//   },

//   // Get user orders
//   getUserOrders: async (userId) => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/order/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       const allOrders = await response.json();
//       return allOrders.filter(order => order.user_id == userId)
//         .sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Sort by date ascending (oldest first)
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       throw error;
//     }
//   },

//   // Get user's transaction history
//   getUserTransactions: async (qbUserId) => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/", {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });

//       if (!response.ok) throw new Error("Failed to fetch transactions");

//       const data = await response.json();
//       // Filter transactions for this specific user
//       const userTransactions = data.filter(t => t.qb_user_id == qbUserId)
//         .sort((a, b) => new Date(b.date || b.systemdate) - new Date(a.date || a.systemdate)); // Latest first

//       return userTransactions;
//     } catch (err) {
//       console.error("Error fetching transactions:", err);
//       return [];
//     }
//   },

//   // Get user's last balance from their transaction history
//   // getUserLastBalance: async (qbUserId) => {
//   //   try {
//   //     const userTransactions = await paymentApiService.getUserTransactions(qbUserId);

//   //     console.log("user tranjection", userTransactions);
//   //     if (userTransactions.length === 0) {
//   //       return 0; // No transactions yet, balance is 0
//   //     }

//   //     // Get the latest transaction with a balance
//   //     const latestTransaction = userTransactions.find(
//   //       t => t.balance !== null && t.balance !== undefined && t.balance !== ""
//   //     );

//   //     if (latestTransaction) {
//   //       return parseFloat(latestTransaction.balance) || 0;
//   //     }

//   //     return 0;
//   //   } catch (err) {
//   //     console.error("Error fetching user balance:", err);
//   //     return 0;
//   //   }
//   // },

//   getUserLastBalance: async (qbUserId) => {
//     try {
//       const userTransactions =
//         await paymentApiService.getUserTransactions(qbUserId);

//       console.log("user transaction", userTransactions);

//       if (!userTransactions || userTransactions.length === 0) {
//         return 0;
//       }

//       // Sort by latest date first
//       const sortedTransactions = [...userTransactions].sort(
//         (a, b) =>
//           new Date(b.systemdate || b.date) -
//           new Date(a.systemdate || a.date)
//       );

//       // Get latest transaction with valid balance
//       const latestTransaction = sortedTransactions.find(
//         (t) =>
//           t.balance !== null &&
//           t.balance !== undefined &&
//           t.balance !== ""
//       );

//       return latestTransaction
//         ? parseFloat(latestTransaction.balance) || 0
//         : 0;
//     } catch (err) {
//       console.error("Error fetching user balance:", err);
//       return 0;
//     }
//   },



//   // Create new transaction
//   createTransaction: async (transactionData) => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/new", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//         body: JSON.stringify(transactionData),
//       });
//       return await response.json();
//     } catch (error) {
//       console.error('Error creating transaction:', error);
//       throw error;
//     }
//   },

//   // Update order payment status and paid amount
//   updateOrderPayment: async (orderId, updateData) => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/order/${orderId}`, {
//         method: "PATCH",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//         body: JSON.stringify(updateData),
//       });
//       return await response.json();
//     } catch (error) {
//       console.error('Error updating order:', error);
//       throw error;
//     }
//   },
// };

// const CreatePaymentPage = () => {
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(false);
//   const [userLoading, setUserLoading] = useState(false);
//   const [users, setUsers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [orders, setOrders] = useState([]);
//   const [unpaidOrders, setUnpaidOrders] = useState([]);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [userBalance, setUserBalance] = useState(0);

//   console.log("lst balanceeeeee", userBalance);
//   const [userTransactions, setUserTransactions] = useState([]);
//   const [paymentAllocation, setPaymentAllocation] = useState([]);
//   const [useAvailableBalance, setUseAvailableBalance] = useState(false);
//   const [amountFromBalance, setAmountFromBalance] = useState(0);

//   // Form state
//   const [formData, setFormData] = useState({
//     date: new Date().toISOString().split("T")[0],
//     customer: null,
//     // New fields
//     paymentMode: "cash", // cash, etransfer, creditcard
//     amount: "", // Total amount received from customer (cash/payment)
//     includesTips: "no", // yes or no
//     tips: "", // Tips amount (only if includesTips is "yes")
//     totalPaid: "", // Calculated total payment (amount + balance used)
//     availableForOrders: "", // Amount available for order payments
//   });

//   // Fetch all users on component mount
//   useEffect(() => {
//     const fetchUsers = async () => {
//       setUserLoading(true);
//       try {
//         const allUsers = await paymentApiService.searchUsers();
//         setUsers(allUsers);
//       } catch (error) {
//         console.error("Error fetching users:", error);
//         toast.error("Failed to load users");
//       } finally {
//         setUserLoading(false);
//       }
//     };

//     fetchUsers();
//   }, []);

//   // Filter users based on search term (client-side filtering)
//   const filteredUsers = useMemo(() => {
//     if (!searchTerm.trim()) return users.slice(0, 20); // Return first 20 users if no search

//     const searchLower = searchTerm.toLowerCase();
//     return users.filter(user =>
//       (user.name && user.name.toLowerCase().includes(searchLower)) ||
//       (user.qb_user_id && user.qb_user_id.toLowerCase().includes(searchLower)) ||
//       (user.phone && user.phone.includes(searchTerm)) ||
//       (user.email && user.email.toLowerCase().includes(searchLower))
//     ).slice(0, 20); // Limit to 20 results
//   }, [users, searchTerm]);

//   // Fetch user data when selected
//   useEffect(() => {
//     const fetchUserData = async () => {
//       if (!selectedUser) {
//         setOrders([]);
//         setUnpaidOrders([]);
//         setUserBalance(0);
//         setUserTransactions([]);
//         setPaymentAllocation([]);
//         setUseAvailableBalance(false);
//         setAmountFromBalance(0);
//         return;
//       }

//       try {
//         // Fetch user's last balance
//         const balance = await paymentApiService.getUserLastBalance(selectedUser.qb_user_id);
//         setUserBalance(balance);

//         // Fetch user's transaction history
//         const transactions = await paymentApiService.getUserTransactions(selectedUser.qb_user_id);
//         setUserTransactions(transactions);

//         // Fetch user's orders
//         const userOrders = await paymentApiService.getUserOrders(selectedUser.id);
//         setOrders(userOrders);

//         // Filter unpaid or partially paid orders
//         const unpaid = userOrders.filter(order =>
//           order.payment_status === "not paid" ||
//           (parseFloat(order.paidamount || 0) < parseFloat(order.total_amount || 0))
//         ).sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Oldest first

//         setUnpaidOrders(unpaid);

//         // Initialize payment allocation
//         const allocation = unpaid.map(order => ({
//           orderId: order.id,
//           qbOrderId: order.qb_order_id,
//           orderDate: order.order_date,
//           totalAmount: parseFloat(order.total_amount || 0),
//           alreadyPaid: parseFloat(order.paidamount || 0),
//           remaining: parseFloat(order.total_amount || 0) - parseFloat(order.paidamount || 0),
//           allocated: 0,
//           newPaidAmount: parseFloat(order.paidamount || 0),
//           newStatus: order.payment_status,
//           originalStatus: order.payment_status
//         }));

//         setPaymentAllocation(allocation);

//       } catch (err) {
//         console.error("Error fetching user data:", err);
//         toast.error("Failed to load customer data");
//       }
//     };

//     fetchUserData();
//   }, [selectedUser]);

//   // Calculate available balance that can be used (only if balance is negative/credit)
//   const availableCredit = useMemo(() => {
//     if (userBalance < 0) {
//       return Math.abs(userBalance); // Convert negative to positive (credit available)
//     }
//     return 0;
//   }, [userBalance]);

//   // Calculate total unpaid amount
//   const calculateTotalUnpaid = () => {
//     return unpaidOrders.reduce((total, order) => {
//       const orderTotal = parseFloat(order.total_amount || 0);
//       const alreadyPaid = parseFloat(order.paidamount || 0);
//       return total + (orderTotal - alreadyPaid);
//     }, 0);
//   };

//   // Calculate total amount needed (positive balance + unpaid orders)
//   const calculateTotalAmountNeeded = () => {
//     let totalNeeded = 0;

//     // If user has positive balance (owes money), add it
//     if (userBalance > 0) {
//       totalNeeded += userBalance;
//     }

//     // Add unpaid orders
//     totalNeeded += calculateTotalUnpaid();

//     return totalNeeded;
//   };

//   // Calculate payment amounts when amount or tips change
//   useEffect(() => {
//     const cashAmount = parseFloat(formData.amount) || 0;
//     const tips = parseFloat(formData.tips) || 0;
//     const balanceToUse = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;

//     if (formData.includesTips === "yes") {
//       // If includes tips: total payment = (cash amount - tips) + balance used
//       const cashForOrders = cashAmount - tips;
//       const totalForOrders = cashForOrders + balanceToUse;

//       setFormData(prev => ({
//         ...prev,
//         totalPaid: cashAmount > 0 ? cashAmount.toFixed(2) : "0.00",
//         availableForOrders: totalForOrders > 0 ? totalForOrders.toFixed(2) : "0.00"
//       }));
//     } else {
//       // If no tips: total payment = cash amount + balance used
//       const totalForOrders = cashAmount + balanceToUse;

//       setFormData(prev => ({
//         ...prev,
//         totalPaid: cashAmount > 0 ? cashAmount.toFixed(2) : "0.00",
//         availableForOrders: totalForOrders > 0 ? totalForOrders.toFixed(2) : "0.00"
//       }));
//     }
//   }, [formData.amount, formData.tips, formData.includesTips, useAvailableBalance, amountFromBalance, availableCredit]);

//   // Calculate payment distribution when available amount changes
//   useEffect(() => {
//     const available = parseFloat(formData.availableForOrders) || 0;
//     if (available <= 0 || paymentAllocation.length === 0) {
//       // Reset allocation if no available amount
//       setPaymentAllocation(prev =>
//         prev.map(order => ({
//           ...order,
//           allocated: 0,
//           newPaidAmount: order.alreadyPaid,
//           newStatus: order.originalStatus
//         }))
//       );
//       return;
//     }

//     let remainingAmount = available;
//     const updatedAllocation = [...paymentAllocation];

//     // Distribute payment across orders (oldest first)
//     for (let i = 0; i < updatedAllocation.length; i++) {
//       const order = updatedAllocation[i];

//       if (remainingAmount <= 0) break;

//       if (order.remaining > 0) {
//         const allocateAmount = Math.min(order.remaining, remainingAmount);
//         order.allocated = allocateAmount;
//         order.newPaidAmount = order.alreadyPaid + allocateAmount;

//         // Check if order is now fully paid
//         if (order.newPaidAmount >= order.totalAmount - 0.01) { // Allow for rounding errors
//           order.newPaidAmount = order.totalAmount;
//           order.newStatus = "paid";
//         } else {
//           order.newStatus = "not paid";
//         }

//         remainingAmount -= allocateAmount;
//       }
//     }

//     setPaymentAllocation(updatedAllocation);

//     // If there's remaining amount after paying all orders, it becomes additional credit
//     if (remainingAmount > 0) {
//       // This will be handled in the new balance calculation
//     }
//   }, [formData.availableForOrders, paymentAllocation.length]);

//   // Handle form input changes
//   const handleInputChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));

//     // If amount changes and includesTips is "no", clear tips
//     if (field === "amount" && formData.includesTips === "no") {
//       setFormData(prev => ({ ...prev, tips: "" }));
//     }

//     // If switching from "yes" to "no" for includesTips, clear tips
//     if (field === "includesTips" && value === "no") {
//       setFormData(prev => ({ ...prev, tips: "" }));
//     }
//   };

//   // Handle user selection
//   const handleUserSelect = (user) => {
//     setSelectedUser(user);
//     setFormData(prev => ({ ...prev, customer: user }));
//     setSelectedOrder(null);
//     setSearchTerm('');
//   };

//   // Handle use available balance change
//   const handleUseBalanceChange = (e) => {
//     const useBalance = e.target.value === "yes";
//     setUseAvailableBalance(useBalance);

//     if (!useBalance) {
//       setAmountFromBalance(0);
//     } else {
//       // Set max amount to available credit
//       setAmountFromBalance(Math.min(availableCredit, calculateTotalUnpaid()));
//     }
//   };

//   // Calculate total allocated amount
//   const calculateTotalAllocated = () => {
//     return paymentAllocation.reduce((total, order) => total + order.allocated, 0);
//   };

//   // Calculate new user balance after payment
//   const calculateNewUserBalance = () => {
//     const cashAmount = parseFloat(formData.amount) || 0;
//     const tips = parseFloat(formData.tips) || 0;
//     const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;
//     const totalAllocated = calculateTotalAllocated();

//     console.log("totalAllocated",totalAllocated);

//     // Current balance (negative = credit, positive = owes)
//     let newBalance = userBalance;

//     // If using available credit (negative balance), it increases (becomes less negative)
//     if (balanceUsed > 0) {
//       newBalance += balanceUsed; // Adding to negative balance makes it less negative
//     }

//     // Cash payment reduces positive balance or increases negative balance (adds credit)
//     const cashForBalance = cashAmount - tips;
//     newBalance -= cashForBalance; // Subtract cash payment from balance

//     // If we allocated less than available for orders, remaining becomes additional credit
//     const remainingAfterAllocation = parseFloat(formData.availableForOrders || 0) - totalAllocated;
//     if (remainingAfterAllocation > 0) {
//       newBalance -= remainingAfterAllocation; // Add as credit
//     }

//     return newBalance;
//   };

//   // Handle form submission
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Validation
//     if (!formData.customer) {
//       toast.error("Please select a customer");
//       return;
//     }

//     // Check if user is making a payment
//     const cashAmount = parseFloat(formData.amount) || 0;
//     const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;

//     if (cashAmount <= 0 && balanceUsed <= 0) {
//       toast.error("Please enter payment amount or use available balance");
//       return;
//     }

//     if (formData.includesTips === "yes" && (!formData.tips || parseFloat(formData.tips) <= 0)) {
//       toast.error("Please enter tips amount");
//       return;
//     }

//     if (formData.includesTips === "yes" && parseFloat(formData.tips) >= cashAmount) {
//       toast.error("Tips cannot be greater than or equal to the cash amount");
//       return;
//     }

//     const totalAmountForOrders = parseFloat(formData.availableForOrders) || 0;

//     if (totalAmountForOrders <= 0) {
//       toast.error("No amount available for order payments");
//       return;
//     }

//     setLoading(true);
//     try {
//       // First, update all affected orders
//       const orderUpdates = [];
//       for (const allocation of paymentAllocation) {
//         if (allocation.allocated > 0) {
//           const updateData = {
//             paidamount: allocation.newPaidAmount.toFixed(2),
//             payment_status: allocation.newStatus,
//             paymentdate: formData.date
//           };

//           try {
//             const result = await paymentApiService.updateOrderPayment(allocation.orderId, updateData);
//             orderUpdates.push({
//               orderId: allocation.orderId,
//               qbOrderId: allocation.qbOrderId,
//               success: true,
//               data: result
//             });
//           } catch (error) {
//             orderUpdates.push({
//               orderId: allocation.orderId,
//               qbOrderId: allocation.qbOrderId,
//               success: false,
//               error: error.message
//             });
//           }
//         }
//       }

//       // Check if any order updates failed
//       const failedUpdates = orderUpdates.filter(update => !update.success);
//       if (failedUpdates.length > 0) {
//         console.error("Failed order updates:", failedUpdates);
//         toast.error(`Failed to update ${failedUpdates.length} order(s). Please check the orders manually.`);
//       }

//       // Calculate new user balance
//       const newUserBalance = calculateNewUserBalance();

//       // Then, create the transaction record
//       const cashAmount = parseFloat(formData.amount) || 0;
//       const tipsAmount = parseFloat(formData.tips) || 0;
//       const balanceUsed = useAvailableBalance ? Math.min(amountFromBalance, availableCredit) : 0;
//       const totalAllocated = calculateTotalAllocated();

//       const transactionPayload = {
//         qb_user_id: formData.customer.qb_user_id,
//         system_user_id: formData.customer.id.toString(),
//         date: formData.date,
//         qb_order_id: orderUpdates.length > 0 ? orderUpdates[0].qbOrderId : "",
//         system_order_id: orderUpdates.length > 0 ? orderUpdates[0].orderId.toString() : "",
//         total: cashAmount.toFixed(2),
//         paid: cashAmount.toFixed(2),
//         tips: tipsAmount.toFixed(2),
//         balance: newUserBalance.toFixed(2),
//         payment_mode: formData.paymentMode,
//         allocated_to_orders: totalAllocated.toFixed(2),
//         balance_used: balanceUsed.toFixed(2),
//         remaining_credit: (parseFloat(formData.availableForOrders || 0) - totalAllocated).toFixed(2)
//       };

//       const transactionResult = await paymentApiService.createTransaction(transactionPayload);

//       // Show success summary
//       const paidOrders = paymentAllocation.filter(order => order.newStatus === "paid" && order.originalStatus !== "paid");
//       const partiallyPaid = paymentAllocation.filter(order => order.newStatus === "not paid" && order.allocated > 0);

//       let successMessage = `Payment processed successfully. `;

//       if (cashAmount > 0) {
//         successMessage += `Cash: $${cashAmount.toFixed(2)}. `;
//       }

//       if (balanceUsed > 0) {
//         successMessage += `Balance used: $${balanceUsed.toFixed(2)}. `;
//       }

//       if (paidOrders.length > 0) {
//         successMessage += `Fully paid ${paidOrders.length} order(s): ${paidOrders.map(o => o.qbOrderId).join(', ')}. `;
//       }

//       if (partiallyPaid.length > 0) {
//         successMessage += `Partially paid ${partiallyPaid.length} order(s). `;
//       }

//       successMessage += `New balance: $${newUserBalance.toFixed(2)}`;

//       if (newUserBalance < 0) {
//         successMessage += ` (Credit: $${Math.abs(newUserBalance).toFixed(2)})`;
//       }

//       toast.success(successMessage);

//       // Reset form
//       setFormData({
//         date: new Date().toISOString().split("T")[0],
//         customer: null,
//         paymentMode: "cash",
//         amount: "",
//         includesTips: "no",
//         tips: "",
//         totalPaid: "",
//         availableForOrders: "",
//       });
//       setSelectedUser(null);
//       setSelectedOrder(null);
//       setSearchTerm("");
//       setOrders([]);
//       setUnpaidOrders([]);
//       setUserBalance(0);
//       setUserTransactions([]);
//       setPaymentAllocation([]);
//       setUseAvailableBalance(false);
//       setAmountFromBalance(0);

//     } catch (error) {
//       console.error("Error submitting payment:", error);
//       toast.error("Failed to submit payment");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatCurrency = (amount) => {
//     return `$${Math.abs(parseFloat(amount || 0)).toLocaleString('en-US', {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     })}`;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString();
//   };

//   const getBalanceType = (balance) => {
//     if (balance < 0) return "credit";
//     if (balance > 0) return "owed";
//     return "zero";
//   };

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Payments" breadcrumbItem="Payment" />

//         <div className="row">
//           <div className="col-12">
//             <div className="card">
//               <div className="card-body">
//                 <h4 className="card-title mb-5 text-center fs-4">Individual Customer Payment Processing</h4>

//                 <form onSubmit={handleSubmit}>
//                   {/* Payment Information */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Date <span className="text-danger">*</span></label>
//                         <input
//                           type="date"
//                           className="form-control"
//                           value={formData.date}
//                           onChange={(e) => handleInputChange('date', e.target.value)}
//                           required
//                         />
//                       </div>
//                     </div>
//                   </div>

//                   {/* Customer Search */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Customer <span className="text-danger">*</span></label>
//                         <div className="position-relative">
//                           <input
//                             type="text"
//                             className="form-control"
//                             value={selectedUser ? `${selectedUser.name} (${selectedUser.qb_user_id})` : searchTerm}
//                             onChange={(e) => {
//                               if (!selectedUser) {
//                                 setSearchTerm(e.target.value);
//                               }
//                             }}
//                             onFocus={() => {
//                               // Clear selection when clicking on input
//                               if (selectedUser) {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }
//                             }}
//                             placeholder={selectedUser ? "Selected customer" : "Search by name, ID, phone, or email..."}
//                           />

//                           {/* Clear selection button when user is selected */}
//                           {selectedUser && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y me-3"
//                               onClick={() => {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }}
//                             >
//                               <i className="fas fa-times"></i>
//                             </button>
//                           )}

//                           {/* User search results dropdown */}
//                           {searchTerm.length >= 1 && !selectedUser && filteredUsers.length > 0 && (
//                             <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-3"
//                               style={{ maxHeight: '250px', overflowY: 'auto' }}>
//                               {filteredUsers.map(user => (
//                                 <div
//                                   key={user.id}
//                                   className="p-2 border-bottom hover-bg-light cursor-pointer"
//                                   onClick={() => {
//                                     handleUserSelect(user);
//                                     setSearchTerm('');
//                                   }}
//                                 >
//                                   <div className="d-flex justify-content-between">
//                                     <span className="fw-medium">{user.name}</span>
//                                     <small className="text-muted">{user.qb_user_id}</small>
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           )}

//                           {userLoading && (
//                             <div className="position-absolute top-50 end-0 translate-middle-y me-3">
//                               <div className="spinner-border spinner-border-sm text-primary" role="status">
//                                 <span className="visually-hidden">Loading...</span>
//                               </div>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Show customer's current balance */}
//                   {selectedUser && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className={`card ${getBalanceType(userBalance) === 'credit' ? 'bg-success bg-opacity-10' : getBalanceType(userBalance) === 'owed' ? 'bg-warning bg-opacity-10' : 'bg-light'}`}>
//                           <div className="card-body">
//                             <h6 className="card-title">
//                               <i className={`fas ${getBalanceType(userBalance) === 'credit' ? 'fa-plus-circle text-success' : getBalanceType(userBalance) === 'owed' ? 'fa-exclamation-circle text-warning' : 'fa-check-circle text-secondary'} me-2`}></i>
//                               Customer Balance
//                             </h6>
//                             <div className="d-flex justify-content-between align-items-center">
//                               <div>
//                                 <p className="mb-0 fs-4">
//                                   {userBalance < 0 ? (
//                                     <span className="text-success fw-bold">
//                                       Credit: {formatCurrency(userBalance)}
//                                     </span>
//                                   ) : userBalance > 0 ? (
//                                     <span className="text-warning fw-bold">
//                                       Owes: {formatCurrency(userBalance)}
//                                     </span>
//                                   ) : (
//                                     <span className="text-secondary fw-bold">
//                                       Balanced: {formatCurrency(userBalance)}
//                                     </span>
//                                   )}
//                                 </p>
//                                 <small className="text-muted">
//                                   {userBalance < 0
//                                     ? "Customer has credit balance (overpaid)"
//                                     : userBalance > 0
//                                       ? "Customer owes this amount"
//                                       : "Customer balance is zero"}
//                                 </small>
//                               </div>
//                               {availableCredit > 0 && (
//                                 <div className="text-end">
//                                   <small className="text-success d-block">
//                                     <i className="fas fa-coins me-1"></i>
//                                     Available Credit: {formatCurrency(availableCredit)}
//                                   </small>
//                                 </div>
//                               )}
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* Show customer's unpaid orders summary */}
//                   {selectedUser && unpaidOrders.length > 0 && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="card bg-warning bg-opacity-10">
//                           <div className="card-body">
//                             <h6 className="card-title">
//                               <i className="fas fa-exclamation-circle me-2"></i>
//                               Customer has {unpaidOrders.length} unpaid/partially paid order(s)
//                             </h6>
//                             <p className="mb-0">
//                               Total unpaid amount: <strong>{formatCurrency(calculateTotalUnpaid())}</strong>
//                             </p>
//                             <small className="text-muted">Payments will be automatically allocated to the oldest unpaid orders first.</small>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* Total Amount Needed */}
//                   {selectedUser && (userBalance > 0 || unpaidOrders.length > 0) && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="card bg-info bg-opacity-10">
//                           <div className="card-body">
//                             <h6 className="card-title">
//                               <i className="fas fa-calculator me-2"></i>
//                               Total Amount Needed
//                             </h6>
//                             <div className="d-flex justify-content-between align-items-center">
//                               <div>
//                                 <p className="mb-0 fs-5 fw-bold">{formatCurrency(calculateTotalAmountNeeded())}</p>
//                                 <small className="text-muted">
//                                   {userBalance > 0 && `Includes balance owed: ${formatCurrency(userBalance)}`}
//                                   {userBalance > 0 && unpaidOrders.length > 0 && ' + '}
//                                   {unpaidOrders.length > 0 && `Unpaid orders: ${formatCurrency(calculateTotalUnpaid())}`}
//                                 </small>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* Use Available Balance */}
//                   {selectedUser && availableCredit > 0 && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="mb-3">
//                           <label className="form-label">Use Available Credit? <span className="text-danger">*</span></label>
//                           <div className="d-flex gap-4 mb-3">
//                             <div className="form-check">
//                               <input
//                                 className="form-check-input"
//                                 type="radio"
//                                 name="useBalance"
//                                 id="useBalanceYes"
//                                 value="yes"
//                                 checked={useAvailableBalance === true}
//                                 onChange={handleUseBalanceChange}
//                                 required
//                               />
//                               <label className="form-check-label" htmlFor="useBalanceYes">
//                                 Yes
//                               </label>
//                             </div>
//                             <div className="form-check">
//                               <input
//                                 className="form-check-input"
//                                 type="radio"
//                                 name="useBalance"
//                                 id="useBalanceNo"
//                                 value="no"
//                                 checked={useAvailableBalance === false}
//                                 onChange={handleUseBalanceChange}
//                               />
//                               <label className="form-check-label" htmlFor="useBalanceNo">
//                                 No
//                               </label>
//                             </div>
//                           </div>

//                           {useAvailableBalance && (
//                             <div className="mt-3">
//                               <label className="form-label">Amount from Credit <span className="text-danger">*</span></label>
//                               <div className="input-group">
//                                 <span className="input-group-text">$</span>
//                                 <input
//                                   type="number"
//                                   className="form-control"
//                                   value={amountFromBalance}
//                                   onChange={(e) => setAmountFromBalance(Math.min(parseFloat(e.target.value) || 0, availableCredit))}
//                                   step="0.01"
//                                   min="0.01"
//                                   max={availableCredit}
//                                   placeholder="0.00"
//                                 />
//                                 <button
//                                   type="button"
//                                   className="btn btn-outline-secondary"
//                                   onClick={() => setAmountFromBalance(Math.min(availableCredit, calculateTotalUnpaid()))}
//                                 >
//                                   Max
//                                 </button>
//                               </div>
//                               <small className="text-muted">
//                                 Available credit: {formatCurrency(availableCredit)}. Max recommended: {formatCurrency(Math.min(availableCredit, calculateTotalUnpaid()))}
//                               </small>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* Payment Mode */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Mode <span className="text-danger">*</span></label>
//                         <div className="d-flex gap-4">
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="cash"
//                               value="cash"
//                               checked={formData.paymentMode === "cash"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                               required
//                             />
//                             <label className="form-check-label" htmlFor="cash">
//                               Cash
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="etransfer"
//                               value="etransfer"
//                               checked={formData.paymentMode === "etransfer"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="etransfer">
//                               E-transfer
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="creditcard"
//                               value="creditcard"
//                               checked={formData.paymentMode === "creditcard"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="creditcard">
//                               Credit/Debit Card
//                             </label>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Cash Amount Field */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Cash/Payment Amount <span className="text-danger">*</span></label>
//                         <div className="input-group">
//                           <span className="input-group-text">$</span>
//                           <input
//                             type="number"
//                             className="form-control"
//                             value={formData.amount}
//                             onChange={(e) => handleInputChange('amount', e.target.value)}
//                             required
//                             step="0.01"
//                             min="0"
//                             placeholder="0.00"
//                           />
//                         </div>
//                         <small className="text-muted">
//                           Enter the cash/payment amount received from customer {useAvailableBalance && '(in addition to credit used)'}
//                         </small>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Includes Tips */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Includes Tips? <span className="text-danger">*</span></label>
//                         <div className="d-flex gap-4">
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="includesTips"
//                               id="tipsYes"
//                               value="yes"
//                               checked={formData.includesTips === "yes"}
//                               onChange={(e) => handleInputChange('includesTips', e.target.value)}
//                               required
//                             />
//                             <label className="form-check-label" htmlFor="tipsYes">
//                               Yes
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="includesTips"
//                               id="tipsNo"
//                               value="no"
//                               checked={formData.includesTips === "no"}
//                               onChange={(e) => handleInputChange('includesTips', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="tipsNo">
//                               No
//                             </label>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Tips Field (only shown if includesTips is "yes") */}
//                   {formData.includesTips === "yes" && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="mb-3">
//                           <label className="form-label">Tips Amount <span className="text-danger">*</span></label>
//                           <div className="input-group">
//                             <span className="input-group-text">$</span>
//                             <input
//                               type="number"
//                               className="form-control"
//                               value={formData.tips}
//                               onChange={(e) => handleInputChange('tips', e.target.value)}
//                               required
//                               step="0.01"
//                               min="0.01"
//                               max={formData.amount}
//                               placeholder="0.00"
//                             />
//                           </div>
//                           <small className="text-muted">Enter tips amount (will be deducted from cash for order payments)</small>
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* Calculated Payment Amount */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="card bg-light">
//                         <div className="card-body">
//                           <h6 className="card-title mb-3">Payment Summary</h6>

//                           <div className="row">
//                             <div className="col-md-6">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Cash/Payment Amount:</span>
//                                   <span className="fw-semibold">{formatCurrency(formData.amount)}</span>
//                                 </div>
//                               </div>
//                             </div>
//                             <div className="col-md-6">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Payment Mode:</span>
//                                   <span className="fw-semibold text-capitalize">
//                                     {formData.paymentMode === "cash" ? "Cash" :
//                                       formData.paymentMode === "etransfer" ? "E-transfer" :
//                                         "Credit/Debit Card"}
//                                   </span>
//                                 </div>
//                               </div>
//                             </div>
//                           </div>

//                           {useAvailableBalance && amountFromBalance > 0 && (
//                             <div className="row">
//                               <div className="col-md-6">
//                                 <div className="mb-2">
//                                   <div className="d-flex justify-content-between">
//                                     <span>Credit Used:</span>
//                                     <span className="fw-semibold text-success">+{formatCurrency(amountFromBalance)}</span>
//                                   </div>
//                                 </div>
//                               </div>
//                             </div>
//                           )}

//                           {formData.includesTips === "yes" && (
//                             <div className="row">
//                               <div className="col-md-6">
//                                 <div className="mb-2">
//                                   <div className="d-flex justify-content-between">
//                                     <span>Tips Deducted:</span>
//                                     <span className="fw-semibold text-warning">-{formatCurrency(formData.tips)}</span>
//                                   </div>
//                                 </div>
//                               </div>
//                             </div>
//                           )}

//                           <div className="row">
//                             <div className="col-md-12">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Total Available for Order Payments:</span>
//                                   <span className="fw-bold fs-5 text-primary">
//                                     {formatCurrency(formData.availableForOrders)}
//                                   </span>
//                                 </div>
//                               </div>
//                             </div>
//                           </div>

//                           <div className="row mt-3">
//                             <div className="col-md-12">
//                               <div className="alert alert-info mb-0">
//                                 <small>
//                                   <strong>Calculation:</strong>
//                                   {formData.includesTips === "yes"
//                                     ? ` $${formData.amount || "0.00"} (Cash)`
//                                     : ` $${formData.amount || "0.00"} (Cash)`}
//                                   {useAvailableBalance && amountFromBalance > 0 && ` + $${amountFromBalance.toFixed(2)} (Credit)`}
//                                   {formData.includesTips === "yes" && ` - $${formData.tips || "0.00"} (Tips)`}
//                                   {` = $${formData.availableForOrders || "0.00"} (For Orders)`}
//                                 </small>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Payment Allocation Preview */}
//                   {selectedUser && paymentAllocation.length > 0 && parseFloat(formData.availableForOrders) > 0 && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="card">
//                           <div className="card-header bg-primary text-white">
//                             <h6 className="mb-0">
//                               <i className="fas fa-exchange-alt me-2"></i>
//                               Payment Allocation (Oldest Orders First)
//                             </h6>
//                           </div>
//                           <div className="card-body">
//                             <div className="table-responsive">
//                               <table className="table table-hover table-sm">
//                                 <thead>
//                                   <tr>
//                                     <th>Order #</th>
//                                     <th>Date</th>
//                                     <th>Total Amount</th>
//                                     <th>Already Paid</th>
//                                     <th>Remaining</th>
//                                     <th>This Payment</th>
//                                     <th>New Paid</th>
//                                     <th>Status</th>
//                                   </tr>
//                                 </thead>
//                                 <tbody>
//                                   {paymentAllocation.map((order, index) => (
//                                     <tr key={order.orderId} className={order.allocated > 0 ? 'table-success' : ''}>
//                                       <td>{order.qbOrderId}</td>
//                                       <td>{formatDate(order.orderDate)}</td>
//                                       <td>{formatCurrency(order.totalAmount)}</td>
//                                       <td>{formatCurrency(order.alreadyPaid)}</td>
//                                       <td>{formatCurrency(order.remaining)}</td>
//                                       <td>
//                                         <span className="fw-bold">{formatCurrency(order.allocated)}</span>
//                                         {order.allocated > 0 && (
//                                           <div className="progress mt-1" style={{ height: '5px' }}>
//                                             <div
//                                               className="progress-bar bg-success"
//                                               style={{
//                                                 width: `${(order.allocated / order.totalAmount) * 100}%`
//                                               }}
//                                             ></div>
//                                           </div>
//                                         )}
//                                       </td>
//                                       <td>{formatCurrency(order.newPaidAmount)}</td>
//                                       <td>
//                                         <span className={`badge ${order.newStatus === 'paid' ? 'bg-success' : 'bg-warning'}`}>
//                                           {order.newStatus === 'paid' ? 'PAID' : 'NOT PAID'}
//                                           {order.originalStatus !== order.newStatus && ' *'}
//                                         </span>
//                                       </td>
//                                     </tr>
//                                   ))}
//                                 </tbody>
//                                 <tfoot>
//                                   <tr className="table-secondary">
//                                     <td colSpan="5" className="text-end fw-bold">Total Allocated:</td>
//                                     <td className="fw-bold">{formatCurrency(calculateTotalAllocated())}</td>
//                                     <td colSpan="2">
//                                       {parseFloat(formData.availableForOrders) - calculateTotalAllocated() > 0 ? (
//                                         <span className="text-info">
//                                           Remaining: {formatCurrency(parseFloat(formData.availableForOrders) - calculateTotalAllocated())}
//                                           <br />
//                                           <small>Will be added to customer credit</small>
//                                         </span>
//                                       ) : (
//                                         <span className="text-success">All allocated</span>
//                                       )}
//                                     </td>
//                                   </tr>
//                                 </tfoot>
//                               </table>
//                             </div>

//                             <div className="alert alert-info mt-3">
//                               <small>
//                                 <i className="fas fa-info-circle me-1"></i>
//                                 <strong>How it works:</strong> Payment is automatically allocated to the oldest unpaid/partially paid orders first.
//                                 Orders marked with * will have their status changed. Remaining amount becomes customer credit for future payments.
//                               </small>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )}

//                   {/* New Balance Preview */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="card bg-light">
//                         <div className="card-body">
//                           <h6 className="card-title">Balance Summary</h6>
//                           <div className="d-flex justify-content-between">
//                             <span>Current Balance:</span>
//                             <span className={`fw-bold ${userBalance < 0 ? 'text-success' : userBalance > 0 ? 'text-warning' : ''}`}>
//                               {userBalance < 0 ? `-${formatCurrency(userBalance)}` : formatCurrency(userBalance)}
//                               {userBalance < 0 && ' (Credit)'}
//                               {userBalance > 0 && ' (Owes)'}
//                             </span>
//                           </div>

//                           {useAvailableBalance && amountFromBalance > 0 && (
//                             <div className="d-flex justify-content-between">
//                               <span>Credit Used:</span>
//                               <span className="fw-bold text-success">-{formatCurrency(amountFromBalance)}</span>
//                             </div>
//                           )}

//                           <div className="d-flex justify-content-between">
//                             <span>Cash Payment:</span>
//                             <span className="fw-bold text-success">-{formatCurrency(formData.amount)}</span>
//                           </div>

//                           {formData.includesTips === "yes" && parseFloat(formData.tips || 0) > 0 && (
//                             <div className="d-flex justify-content-between">
//                               <span>Tips:</span>
//                               <span className="fw-bold text-warning">+{formatCurrency(formData.tips)}</span>
//                             </div>
//                           )}

//                           {parseFloat(formData.availableForOrders || 0) - calculateTotalAllocated() > 0 && (
//                             <div className="d-flex justify-content-between">
//                               <span>Additional Credit:</span>
//                               <span className="fw-bold text-success">-{formatCurrency(parseFloat(formData.availableForOrders || 0) - calculateTotalAllocated())}</span>
//                             </div>
//                           )}

//                           <hr />

//                           <div className="d-flex justify-content-between">
//                             <span className="fw-bold">New Balance:</span>
//                             <span className={`fw-bold fs-5 ${calculateNewUserBalance() < 0 ? 'text-success' : calculateNewUserBalance() > 0 ? 'text-warning' : ''}`}>
//                               {calculateNewUserBalance() < 0 ? `-${formatCurrency(calculateNewUserBalance())}` : formatCurrency(calculateNewUserBalance())}
//                               {calculateNewUserBalance() < 0 && ' (Credit)'}
//                               {calculateNewUserBalance() > 0 && ' (Owes)'}
//                             </span>
//                           </div>

//                           <div className="alert alert-info mt-3 mb-0">
//                             <small>
//                               <strong>Balance Logic:</strong>
//                               {userBalance < 0 && ` Customer has ${formatCurrency(Math.abs(userBalance))} credit.`}
//                               {userBalance > 0 && ` Customer owes ${formatCurrency(userBalance)}.`}
//                               {userBalance === 0 && ` Customer balance is zero.`}
//                               {' Payments reduce owed amount or increase credit.'}
//                             </small>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Form Actions */}
//                   <div className="row">
//                     <div className="col-12">
//                       <div className="d-flex justify-content-between">
//                         <div className="d-flex gap-2">
//                           <button
//                             type="submit"
//                             className="btn btn-success px-4"
//                             disabled={loading || !formData.customer || (!formData.amount && !useAvailableBalance)}
//                           >
//                             {loading ? (
//                               <>
//                                 <i className="fas fa-spinner fa-spin me-1"></i>
//                                 Processing Payment...
//                               </>
//                             ) : (
//                               <>
//                                 <i className="fas fa-check-circle me-1"></i>
//                                 Process Payment
//                               </>
//                             )}
//                           </button>

//                           <button
//                             type="button"
//                             className="btn btn-secondary"
//                             onClick={() => {
//                               setFormData({
//                                 date: new Date().toISOString().split("T")[0],
//                                 customer: null,
//                                 paymentMode: "cash",
//                                 amount: "",
//                                 includesTips: "no",
//                                 tips: "",
//                                 totalPaid: "",
//                                 availableForOrders: "",
//                               });
//                               setSelectedUser(null);
//                               setSelectedOrder(null);
//                               setSearchTerm("");
//                               setOrders([]);
//                               setUnpaidOrders([]);
//                               setUserBalance(0);
//                               setUserTransactions([]);
//                               setPaymentAllocation([]);
//                               setUseAvailableBalance(false);
//                               setAmountFromBalance(0);
//                             }}
//                             disabled={loading}
//                           >
//                             <i className="fas fa-redo me-1"></i>
//                             Reset
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CreatePaymentPage;








// import React, { useState, useEffect, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import { toast } from "sonner";
// import Breadcrumb from "components/Common/Breadcrumb";
// import DashboardLoader from "components/Loader/DashboardLoader";

// // API Service for Payments
// const paymentApiService = {
//   // Search users - fetches all then filters locally since API doesn't support search
//   searchUsers: async () => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/userProfile/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       if (!response.ok) {
//         throw new Error("Failed to fetch users");
//       }
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching users:', error);
//       throw error;
//     }
//   },

//   // Get user orders
//   getUserOrders: async (userId) => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/order/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       const allOrders = await response.json();
//       return allOrders.filter(order => order.user_id == userId)
//         .sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Sort by date ascending (oldest first)
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       throw error;
//     }
//   },

//   // Get last transaction balance
//   getLastBalance: async () => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/", {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });

//       if (!response.ok) throw new Error("Failed to fetch transactions");

//       const data = await response.json();
//       let lastBalance = 0;

//       if (data && data.length > 0) {
//         const sortedTransactions = [...data].sort(
//           (a, b) =>
//             new Date(b.systemdate || b.date) - new Date(a.systemdate || a.date)
//         );

//         const lastTransactionWithBalance = sortedTransactions.find(
//           (t) =>
//             t.balance !== null && t.balance !== undefined && t.balance !== ""
//         );

//         if (lastTransactionWithBalance) {
//           lastBalance = parseFloat(lastTransactionWithBalance.balance) || 0;
//         }
//       }

//       return lastBalance;
//     } catch (err) {
//       console.error("Error fetching balance:", err);
//       return 0;
//     }
//   },

//   // Create new transaction
//   createTransaction: async (transactionData) => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/new", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//         body: JSON.stringify(transactionData),
//       });
//       return await response.json();
//     } catch (error) {
//       console.error('Error creating transaction:', error);
//       throw error;
//     }
//   },

//   // Update order payment status and paid amount
//   updateOrderPayment: async (orderId, updateData) => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/order/${orderId}`, {
//         method: "PATCH",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//         body: JSON.stringify(updateData),
//       });
//       return await response.json();
//     } catch (error) {
//       console.error('Error updating order:', error);
//       throw error;
//     }
//   },
// };

// const CreatePaymentPage = () => {
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(false);
//   const [userLoading, setUserLoading] = useState(false);
//   const [users, setUsers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [orders, setOrders] = useState([]);
//   const [unpaidOrders, setUnpaidOrders] = useState([]);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [lastBalance, setLastBalance] = useState(0);
//   const [paymentAllocation, setPaymentAllocation] = useState([]);

//   // Form state
//   const [formData, setFormData] = useState({
//     date: new Date().toISOString().split("T")[0],
//     customer: null,
//     // New fields
//     paymentMode: "cash", // cash, etransfer, creditcard
//     amount: "", // Total amount received from customer
//     includesTips: "no", // yes or no
//     tips: "", // Tips amount (only if includesTips is "yes")
//     totalPaid: "", // Calculated payment amount (amount - tips)
//     availableForOrders: "", // Amount available for order payments
//     receivedby: ""
//   });

//   // Fetch all users on component mount
//   useEffect(() => {
//     const fetchUsers = async () => {
//       setUserLoading(true);
//       try {
//         const allUsers = await paymentApiService.searchUsers();
//         setUsers(allUsers);
//       } catch (error) {
//         console.error("Error fetching users:", error);
//         toast.error("Failed to load users");
//       } finally {
//         setUserLoading(false);
//       }
//     };

//     const fetchLastBalance = async () => {
//       const balance = await paymentApiService.getLastBalance();
//       setLastBalance(balance);
//     };

//     fetchUsers();
//     fetchLastBalance();
//   }, []);

//   // Filter users based on search term (client-side filtering)
//   const filteredUsers = useMemo(() => {
//     if (!searchTerm.trim()) return users.slice(0, 20); // Return first 20 users if no search

//     const searchLower = searchTerm.toLowerCase();
//     return users.filter(user =>
//       (user.name && user.name.toLowerCase().includes(searchLower)) ||
//       (user.qb_user_id && user.qb_user_id.toLowerCase().includes(searchLower)) ||
//       (user.phone && user.phone.includes(searchTerm)) ||
//       (user.email && user.email.toLowerCase().includes(searchLower))
//     ).slice(0, 20); // Limit to 20 results
//   }, [users, searchTerm]);

//   // Calculate payment amounts when amount or tips change
//   useEffect(() => {
//     const amount = parseFloat(formData.amount) || 0;
//     const tips = parseFloat(formData.tips) || 0;

//     if (formData.includesTips === "yes") {
//       // If includes tips, payment amount = total amount - tips
//       const calculatedPayment = amount - tips;
//       setFormData(prev => ({
//         ...prev,
//         totalPaid: calculatedPayment > 0 ? calculatedPayment.toFixed(2) : "0.00",
//         availableForOrders: calculatedPayment > 0 ? calculatedPayment.toFixed(2) : "0.00"
//       }));
//     } else {
//       // If doesn't include tips, payment amount = total amount
//       setFormData(prev => ({
//         ...prev,
//         totalPaid: amount > 0 ? amount.toFixed(2) : "0.00",
//         availableForOrders: amount > 0 ? amount.toFixed(2) : "0.00"
//       }));
//     }
//   }, [formData.amount, formData.tips, formData.includesTips]);

//   // Fetch orders when a user is selected
//   useEffect(() => {
//     const fetchUserOrders = async () => {
//       if (!selectedUser) {
//         setOrders([]);
//         setUnpaidOrders([]);
//         setPaymentAllocation([]);
//         return;
//       }

//       try {
//         const userOrders = await paymentApiService.getUserOrders(selectedUser.id);
//         setOrders(userOrders);

//         // Filter unpaid or partially paid orders
//         const unpaid = userOrders.filter(order =>
//           order.payment_status === "not paid" ||
//           (parseFloat(order.paidamount || 0) < parseFloat(order.total_amount || 0))
//         ).sort((a, b) => new Date(a.order_date) - new Date(b.order_date)); // Oldest first

//         setUnpaidOrders(unpaid);

//         // Initialize payment allocation
//         const allocation = unpaid.map(order => ({
//           orderId: order.id,
//           qbOrderId: order.qb_order_id,
//           orderDate: order.order_date,
//           totalAmount: parseFloat(order.total_amount || 0),
//           alreadyPaid: parseFloat(order.paidamount || 0),
//           remaining: parseFloat(order.total_amount || 0) - parseFloat(order.paidamount || 0),
//           allocated: 0,
//           newPaidAmount: parseFloat(order.paidamount || 0),
//           newStatus: order.payment_status,
//           originalStatus: order.payment_status
//         }));

//         setPaymentAllocation(allocation);

//       } catch (err) {
//         console.error("Error fetching orders:", err);
//         toast.error("Failed to load customer orders");
//       }
//     };

//     fetchUserOrders();
//   }, [selectedUser]);

//   // Calculate payment distribution when available amount changes
//   useEffect(() => {
//     const available = parseFloat(formData.availableForOrders) || 0;
//     if (available <= 0 || paymentAllocation.length === 0) {
//       // Reset allocation if no available amount
//       setPaymentAllocation(prev =>
//         prev.map(order => ({
//           ...order,
//           allocated: 0,
//           newPaidAmount: order.alreadyPaid,
//           newStatus: order.originalStatus
//         }))
//       );
//       return;
//     }

//     let remainingAmount = available;
//     const updatedAllocation = [...paymentAllocation];

//     // Distribute payment across orders (oldest first)
//     for (let i = 0; i < updatedAllocation.length; i++) {
//       const order = updatedAllocation[i];

//       if (remainingAmount <= 0) break;

//       if (order.remaining > 0) {
//         const allocateAmount = Math.min(order.remaining, remainingAmount);
//         order.allocated = allocateAmount;
//         order.newPaidAmount = order.alreadyPaid + allocateAmount;

//         // Check if order is now fully paid
//         if (order.newPaidAmount >= order.totalAmount - 0.01) { // Allow for rounding errors
//           order.newPaidAmount = order.totalAmount;
//           order.newStatus = "paid";
//         } else {
//           order.newStatus = "not paid";
//         }

//         remainingAmount -= allocateAmount;
//       }
//     }

//     setPaymentAllocation(updatedAllocation);

//     // If there's remaining amount after paying all orders, add as credit
//     if (remainingAmount > 0) {
//       toast.info(`$${remainingAmount.toFixed(2)} will remain as customer credit`);
//     }
//   }, [formData.availableForOrders, paymentAllocation.length]);

//   // Handle form input changes
//   const handleInputChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));

//     // If amount changes and includesTips is "no", clear tips
//     if (field === "amount" && formData.includesTips === "no") {
//       setFormData(prev => ({ ...prev, tips: "" }));
//     }

//     // If switching from "yes" to "no" for includesTips, clear tips
//     if (field === "includesTips" && value === "no") {
//       setFormData(prev => ({ ...prev, tips: "" }));
//     }
//   };

//   // Handle user selection
//   const handleUserSelect = (user) => {
//     setSelectedUser(user);
//     setFormData(prev => ({ ...prev, customer: user }));
//     setSelectedOrder(null);
//     setSearchTerm('');
//   };

//   // Calculate total unpaid amount
//   const calculateTotalUnpaid = () => {
//     return unpaidOrders.reduce((total, order) => {
//       const orderTotal = parseFloat(order.total_amount || 0);
//       const alreadyPaid = parseFloat(order.paidamount || 0);
//       return total + (orderTotal - alreadyPaid);
//     }, 0);
//   };

//   // Calculate total allocated amount
//   const calculateTotalAllocated = () => {
//     return paymentAllocation.reduce((total, order) => total + order.allocated, 0);
//   };

//   // Handle form submission
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Validation
//     if (!formData.customer) {
//       toast.error("Please select a customer");
//       return;
//     }

//     if (!formData.amount || parseFloat(formData.amount) <= 0) {
//       toast.error("Please enter a valid amount");
//       return;
//     }

//     if (formData.includesTips === "yes" && (!formData.tips || parseFloat(formData.tips) <= 0)) {
//       toast.error("Please enter tips amount");
//       return;
//     }

//     if (formData.includesTips === "yes" && parseFloat(formData.tips) >= parseFloat(formData.amount)) {
//       toast.error("Tips cannot be greater than or equal to the total amount");
//       return;
//     }

//     const totalAmount = parseFloat(formData.amount) || 0;
//     const tipsAmount = parseFloat(formData.tips) || 0;
//     const paymentAmount = parseFloat(formData.totalPaid) || 0;
//     const availableForOrders = parseFloat(formData.availableForOrders) || 0;

//     // Additional validation
//     if (formData.includesTips === "yes" && Math.abs((paymentAmount + tipsAmount) - totalAmount) > 0.01) {
//       toast.error("Payment calculation error. Please check amounts.");
//       return;
//     }

//     if (availableForOrders <= 0) {
//       toast.error("No amount available for order payments");
//       return;
//     }

//     setLoading(true);
//     try {
//       // First, update all affected orders
//       const orderUpdates = [];
//       for (const allocation of paymentAllocation) {
//         if (allocation.allocated > 0) {
//           const updateData = {
//             paidamount: allocation.newPaidAmount.toFixed(2),
//             payment_status: allocation.newStatus,
//             paymentdate: formData.date
//           };

//           try {
//             const result = await paymentApiService.updateOrderPayment(allocation.orderId, updateData);
//             orderUpdates.push({
//               orderId: allocation.orderId,
//               qbOrderId: allocation.qbOrderId,
//               success: true,
//               data: result
//             });
//           } catch (error) {
//             orderUpdates.push({
//               orderId: allocation.orderId,
//               qbOrderId: allocation.qbOrderId,
//               success: false,
//               error: error.message
//             });
//           }
//         }
//       }

//       // Check if any order updates failed
//       const failedUpdates = orderUpdates.filter(update => !update.success);
//       if (failedUpdates.length > 0) {
//         console.error("Failed order updates:", failedUpdates);
//         toast.error(`Failed to update ${failedUpdates.length} order(s). Please check the orders manually.`);
//       }

//       // Then, create the transaction record
//       const newBalance = (lastBalance + paymentAmount + tipsAmount).toFixed(2);
//       const totalAllocated = calculateTotalAllocated();

//       const transactionPayload = {
//         qb_user_id: formData.customer.qb_user_id,
//         system_user_id: formData.customer.id.toString(),
//         date: formData.date,
//         qb_order_id: orderUpdates.length > 0 ? orderUpdates[0].qbOrderId : "",
//         system_order_id: orderUpdates.length > 0 ? orderUpdates[0].orderId.toString() : "",
//         total: paymentAmount.toFixed(2),
//         paid: paymentAmount.toFixed(2),
//         tips: tipsAmount.toFixed(2),
//         balance: newBalance,
//         payment_mode: formData.paymentMode,
//         allocated_to_orders: totalAllocated.toFixed(2),
//         remaining_credit: (availableForOrders - totalAllocated).toFixed(2)
//       };

//       const transactionResult = await paymentApiService.createTransaction(transactionPayload);

//       // Show success summary
//       const paidOrders = paymentAllocation.filter(order => order.newStatus === "paid" && order.originalStatus !== "paid");
//       const partiallyPaid = paymentAllocation.filter(order => order.newStatus === "not paid" && order.allocated > 0);

//       let successMessage = `Payment of $${paymentAmount.toFixed(2)} processed successfully.`;

//       if (paidOrders.length > 0) {
//         successMessage += ` Fully paid ${paidOrders.length} order(s): ${paidOrders.map(o => o.qbOrderId).join(', ')}.`;
//       }

//       if (partiallyPaid.length > 0) {
//         successMessage += ` Partially paid ${partiallyPaid.length} order(s).`;
//       }

//       if (availableForOrders - totalAllocated > 0) {
//         successMessage += ` $${(availableForOrders - totalAllocated).toFixed(2)} remains as customer credit.`;
//       }

//       toast.success(successMessage);

//       // Reset form
//       setFormData({
//         date: new Date().toISOString().split("T")[0],
//         customer: null,
//         paymentMode: "cash",
//         amount: "",
//         includesTips: "no",
//         tips: "",
//         totalPaid: "",
//         availableForOrders: "",
//       });
//       setSelectedUser(null);
//       setSelectedOrder(null);
//       setSearchTerm("");
//       setOrders([]);
//       setUnpaidOrders([]);
//       setPaymentAllocation([]);

//       // Update last balance for next transaction
//       setLastBalance(parseFloat(newBalance));

//     } catch (error) {
//       console.error("Error submitting payment:", error);
//       toast.error("Failed to submit payment");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatCurrency = (amount) => {
//     return `$${parseFloat(amount || 0).toLocaleString('en-US', {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     })}`;
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString();
//   };

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Payments" breadcrumbItem="Payment" />

//         <div className="row">
//           <div className="col-12">
//             <div className="card">
//               <div className="card-body">
//                 <h4 className="card-title mb-5 text-center fs-4">Payment Processing</h4>

//                 <form onSubmit={handleSubmit}>

//                   {/* Customer Search */}
//                   <div className="row mb-4">
//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Payment From <span className="text-danger">*</span></label>
//                         <div className="position-relative">
//                           <input
//                             type="text"
//                             className="form-control"
//                             value={selectedUser ? `${selectedUser.name} (${selectedUser.qb_user_id})` : searchTerm}
//                             onChange={(e) => {
//                               if (!selectedUser) {
//                                 setSearchTerm(e.target.value);
//                               }
//                             }}
//                             onFocus={() => {
//                               // Clear selection when clicking on input
//                               if (selectedUser) {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }
//                             }}
//                             placeholder={selectedUser ? "Selected customer" : "Search by name, ID, phone, or email..."}
//                           />

//                           {/* Clear selection button when user is selected */}
//                           {selectedUser && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y me-3"
//                               onClick={() => {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }}
//                             >
//                               <i className="fas fa-times"></i>
//                             </button>
//                           )}

//                           {/* User search results dropdown */}
//                           {searchTerm.length >= 1 && !selectedUser && filteredUsers.length > 0 && (
//                             <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-3"
//                               style={{ maxHeight: '250px', overflowY: 'auto' }}>
//                               {filteredUsers.map(user => (
//                                 <div
//                                   key={user.id}
//                                   className="p-2 border-bottom hover-bg-light cursor-pointer"
//                                   onClick={() => {
//                                     handleUserSelect(user);
//                                     setSearchTerm('');
//                                   }}
//                                 >
//                                   <div className="d-flex justify-content-between">
//                                     <span className="fw-medium">{user.name}</span>
//                                     <small className="text-muted">{user.qb_user_id}</small>
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           )}

//                           {userLoading && (
//                             <div className="position-absolute top-50 end-0 translate-middle-y me-3">
//                               <div className="spinner-border spinner-border-sm text-primary" role="status">
//                                 <span className="visually-hidden">Loading...</span>
//                               </div>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>

//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Date <span className="text-danger">*</span></label>
//                         <input
//                           type="date"
//                           className="form-control"
//                           value={formData.date}
//                           onChange={(e) => handleInputChange('date', e.target.value)}
//                           required
//                         />
//                       </div>
//                     </div>

//                   </div>



//                   {/* Show customer's unpaid orders summary */}
//                   {/* {selectedUser && unpaidOrders.length > 0 && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="card bg-warning bg-opacity-10">
//                           <div className="card-body">
//                             <h6 className="card-title">
//                               <i className="fas fa-exclamation-circle me-2"></i>
//                               Customer has {unpaidOrders.length} unpaid/partially paid order(s)
//                             </h6>
//                             <p className="mb-0">
//                               Total unpaid amount: <strong>{formatCurrency(calculateTotalUnpaid())}</strong>
//                             </p>
//                             <small className="text-muted">Payments will be automatically allocated to the oldest unpaid orders first.</small>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )} */}

//                   {/* Payment Mode */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Mode <span className="text-danger">*</span></label>
//                         <div className="d-flex gap-4">
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="cash"
//                               value="cash"
//                               checked={formData.paymentMode === "cash"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                               required
//                             />
//                             <label className="form-check-label" htmlFor="cash">
//                               Cash
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="etransfer"
//                               value="etransfer"
//                               checked={formData.paymentMode === "etransfer"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="etransfer">
//                               E-transfer
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="paymentMode"
//                               id="creditcard"
//                               value="creditcard"
//                               checked={formData.paymentMode === "creditcard"}
//                               onChange={(e) => handleInputChange('paymentMode', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="creditcard">
//                               Credit/Debit Card
//                             </label>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Amount Field */}
//                   <div className="row mb-4">
//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Total Amount Received <span className="text-danger">*</span></label>
//                         <div className="input-group">
//                           <span className="input-group-text">$</span>
//                           <input
//                             type="number"
//                             className="form-control"
//                             value={formData.amount}
//                             onChange={(e) => handleInputChange('amount', e.target.value)}
//                             required
//                             step="0.01"
//                             min="0.01"
//                             placeholder="0.00"
//                           />
//                         </div>
//                         <small className="text-muted">Enter the total amount received from customer</small>
//                       </div>
//                     </div>

//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Received By <span className="text-danger"></span></label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={formData.receivedby}
//                           onChange={(e) => handleInputChange('receivedby', e.target.value)}
//                           required
//                         />
//                       </div>
//                     </div>


//                   </div>

//                   {/* Includes Tips */}
//                   <div className="row mb-4">
//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Includes Tips? <span className="text-danger">*</span></label>
//                         <div className="d-flex gap-4">
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="includesTips"
//                               id="tipsYes"
//                               value="yes"
//                               checked={formData.includesTips === "yes"}
//                               onChange={(e) => handleInputChange('includesTips', e.target.value)}
//                               required
//                             />
//                             <label className="form-check-label" htmlFor="tipsYes">
//                               Yes
//                             </label>
//                           </div>
//                           <div className="form-check">
//                             <input
//                               className="form-check-input"
//                               type="radio"
//                               name="includesTips"
//                               id="tipsNo"
//                               value="no"
//                               checked={formData.includesTips === "no"}
//                               onChange={(e) => handleInputChange('includesTips', e.target.value)}
//                             />
//                             <label className="form-check-label" htmlFor="tipsNo">
//                               No
//                             </label>
//                           </div>
//                         </div>
//                       </div>
//                     </div>

//                     {formData.includesTips === "yes" && (

//                       <div className="col-md-6">
//                         <div className="mb-3">
//                           <label className="form-label">Tips Amount <span className="text-danger">*</span></label>
//                           <div className="input-group">
//                             <span className="input-group-text">$</span>
//                             <input
//                               type="number"
//                               className="form-control"
//                               value={formData.tips}
//                               onChange={(e) => handleInputChange('tips', e.target.value)}
//                               required
//                               step="0.01"
//                               min="0.01"
//                               max={formData.amount}
//                               placeholder="0.00"
//                             />
//                           </div>
//                           <small className="text-muted">Enter tips amount (will be deducted from total for order payments)</small>
//                         </div>

//                       </div>
//                     )}

//                   </div>

//                   {/* Tips Field (only shown if includesTips is "yes") */}


//                   {/* Calculated Payment Amount */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="card bg-light">
//                         <div className="card-body">
//                           <h6 className="card-title mb-3">Payment Summary</h6>

//                           <div className="row">
//                             <div className="col-md-6">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Total Amount Received:</span>
//                                   <span className="fw-semibold">{formatCurrency(formData.amount)}</span>
//                                 </div>
//                               </div>
//                             </div>
//                             <div className="col-md-6">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Payment Mode:</span>
//                                   <span className="fw-semibold text-capitalize">
//                                     {formData.paymentMode === "cash" ? "Cash" :
//                                       formData.paymentMode === "etransfer" ? "E-transfer" :
//                                         "Credit/Debit Card"}
//                                   </span>
//                                 </div>
//                               </div>
//                             </div>
//                           </div>

//                           {formData.includesTips === "yes" && (
//                             <div className="row">
//                               <div className="col-md-6">
//                                 <div className="mb-2">
//                                   <div className="d-flex justify-content-between">
//                                     <span>Tips Deducted:</span>
//                                     <span className="fw-semibold text-success">-{formatCurrency(formData.tips)}</span>
//                                   </div>
//                                 </div>
//                               </div>
//                             </div>
//                           )}

//                           <div className="row">
//                             <div className="col-md-12">
//                               <div className="mb-2">
//                                 <div className="d-flex justify-content-between">
//                                   <span>Amount Available for Order Payments:</span>
//                                   <span className="fw-bold fs-5 text-primary">
//                                     {formatCurrency(formData.availableForOrders)}
//                                   </span>
//                                 </div>
//                               </div>
//                             </div>
//                           </div>

//                           <div className="row mt-3">
//                             <div className="col-md-12">
//                               <div className="alert alert-info mb-0">
//                                 <small>
//                                   <strong>Calculation:</strong>
//                                   {formData.includesTips === "yes"
//                                     ? ` $${formData.amount || "0.00"} (Received) - $${formData.tips || "0.00"} (Tips) = $${formData.availableForOrders || "0.00"} (For Orders)`
//                                     : ` $${formData.amount || "0.00"} (Received) = $${formData.availableForOrders || "0.00"} (For Orders)`}
//                                 </small>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Payment Allocation Preview */}
//                   {/* {selectedUser && paymentAllocation.length > 0 && parseFloat(formData.availableForOrders) > 0 && (
//                     <div className="row mb-4">
//                       <div className="col-md-12">
//                         <div className="card">
//                           <div className="card-header bg-primary text-white">
//                             <h6 className="mb-0">
//                               <i className="fas fa-exchange-alt me-2"></i>
//                               Payment Allocation (Oldest Orders First)
//                             </h6>
//                           </div>
//                           <div className="card-body">
//                             <div className="table-responsive">
//                               <table className="table table-hover table-sm">
//                                 <thead>
//                                   <tr>
//                                     <th>Order #</th>
//                                     <th>Date</th>
//                                     <th>Total Amount</th>
//                                     <th>Already Paid</th>
//                                     <th>Remaining</th>
//                                     <th>This Payment</th>
//                                     <th>New Paid</th>
//                                     <th>Status</th>
//                                   </tr>
//                                 </thead>
//                                 <tbody>
//                                   {paymentAllocation.map((order, index) => (
//                                     <tr key={order.orderId} className={order.allocated > 0 ? 'table-success' : ''}>
//                                       <td>{order.qbOrderId}</td>
//                                       <td>{formatDate(order.orderDate)}</td>
//                                       <td>{formatCurrency(order.totalAmount)}</td>
//                                       <td>{formatCurrency(order.alreadyPaid)}</td>
//                                       <td>{formatCurrency(order.remaining)}</td>
//                                       <td>
//                                         <span className="fw-bold">{formatCurrency(order.allocated)}</span>
//                                         {order.allocated > 0 && (
//                                           <div className="progress mt-1" style={{ height: '5px' }}>
//                                             <div
//                                               className="progress-bar bg-success"
//                                               style={{
//                                                 width: `${(order.allocated / order.totalAmount) * 100}%`
//                                               }}
//                                             ></div>
//                                           </div>
//                                         )}
//                                       </td>
//                                       <td>{formatCurrency(order.newPaidAmount)}</td>
//                                       <td>
//                                         <span className={`badge ${order.newStatus === 'paid' ? 'bg-success' : 'bg-warning'}`}>
//                                           {order.newStatus === 'paid' ? 'PAID' : 'NOT PAID'}
//                                           {order.originalStatus !== order.newStatus && ' *'}
//                                         </span>
//                                       </td>
//                                     </tr>
//                                   ))}
//                                 </tbody>
//                                 <tfoot>
//                                   <tr className="table-secondary">
//                                     <td colSpan="5" className="text-end fw-bold">Total Allocated:</td>
//                                     <td className="fw-bold">{formatCurrency(calculateTotalAllocated())}</td>
//                                     <td colSpan="2">
//                                       {parseFloat(formData.availableForOrders) - calculateTotalAllocated() > 0 ? (
//                                         <span className="text-info">
//                                           Remaining: {formatCurrency(parseFloat(formData.availableForOrders) - calculateTotalAllocated())}
//                                         </span>
//                                       ) : (
//                                         <span className="text-success">All allocated</span>
//                                       )}
//                                     </td>
//                                   </tr>
//                                 </tfoot>
//                               </table>
//                             </div>

//                             <div className="alert alert-info mt-3">
//                               <small>
//                                 <i className="fas fa-info-circle me-1"></i>
//                                 <strong>How it works:</strong> Payment is automatically allocated to the oldest unpaid/partially paid orders first.
//                                 Orders marked with * will have their status changed. Remaining amount becomes customer credit for future payments.
//                               </small>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   )} */}

//                   {/* New Balance Preview */}
//                   {/* <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="card bg-light">
//                         <div className="card-body">
//                           <h6 className="card-title">Balance Summary</h6>
//                           <div className="d-flex justify-content-between">
//                             <span>Current Balance:</span>
//                             <span className="fw-bold">{formatCurrency(lastBalance)}</span>
//                           </div>
//                           <div className="d-flex justify-content-between">
//                             <span>Payment Amount:</span>
//                             <span className="fw-bold text-success">+{formatCurrency(formData.totalPaid)}</span>
//                           </div>
//                           {formData.includesTips === "yes" && parseFloat(formData.tips || 0) > 0 && (
//                             <div className="d-flex justify-content-between">
//                               <span>Tips:</span>
//                               <span className="fw-bold text-success">+{formatCurrency(formData.tips)}</span>
//                             </div>
//                           )}
//                           <hr />
//                           <div className="d-flex justify-content-between">
//                             <span className="fw-bold">New Balance:</span>
//                             <span className="fw-bold fs-5">
//                               {formatCurrency(
//                                 lastBalance +
//                                 parseFloat(formData.totalPaid || 0) +
//                                 (formData.includesTips === "yes" ? parseFloat(formData.tips || 0) : 0)
//                               )}
//                             </span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div> */}

//                   {/* Form Actions */}
//                   <div className="row">
//                     <div className="col-12">
//                       <div className="d-flex justify-content-between">
//                         <div className="d-flex gap-2">
//                           <button
//                             type="submit"
//                             className="btn btn-success px-4"
//                             disabled={loading || !formData.customer || !formData.amount || !formData.totalPaid}
//                           >
//                             {loading ? (
//                               <>
//                                 <i className="fas fa-spinner fa-spin me-1"></i>
//                                 Processing Payment...
//                               </>
//                             ) : (
//                               <>
//                                 <i className="fas fa-check-circle me-1"></i>
//                                 Process Smart Payment
//                               </>
//                             )}
//                           </button>

//                           <button
//                             type="button"
//                             className="btn btn-secondary"
//                             onClick={() => {
//                               setFormData({
//                                 date: new Date().toISOString().split("T")[0],
//                                 customer: null,
//                                 paymentMode: "cash",
//                                 amount: "",
//                                 includesTips: "no",
//                                 tips: "",
//                                 totalPaid: "",
//                                 availableForOrders: "",
//                               });
//                               setSelectedUser(null);
//                               setSelectedOrder(null);
//                               setSearchTerm("");
//                               setOrders([]);
//                               setUnpaidOrders([]);
//                               setPaymentAllocation([]);
//                             }}
//                             disabled={loading}
//                           >
//                             <i className="fas fa-redo me-1"></i>
//                             Reset
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CreatePaymentPage;










// import React, { useState, useEffect, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import { toast } from "sonner";
// import Breadcrumb from "components/Common/Breadcrumb";
// import DashboardLoader from "components/Loader/DashboardLoader";

// // API Service for Payments
// const paymentApiService = {
//   // Search users - fetches all then filters locally since API doesn't support search
//   searchUsers: async () => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/userProfile/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       if (!response.ok) {
//         throw new Error("Failed to fetch users");
//       }
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching users:', error);
//       throw error;
//     }
//   },

//   // Get user orders
//   getUserOrders: async (userId) => {
//     try {
//       const response = await fetch(`https://qwikbistro.shop/order/`, {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });
//       const allOrders = await response.json();
//       return allOrders.filter(order => order.user_id == userId);
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       throw error;
//     }
//   },

//   // Get last transaction balance
//   getLastBalance: async () => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/", {
//         headers: {
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//       });

//       if (!response.ok) throw new Error("Failed to fetch transactions");

//       const data = await response.json();
//       let lastBalance = 0;

//       if (data && data.length > 0) {
//         const sortedTransactions = [...data].sort(
//           (a, b) =>
//             new Date(b.systemdate || b.date) - new Date(a.systemdate || a.date)
//         );

//         const lastTransactionWithBalance = sortedTransactions.find(
//           (t) =>
//             t.balance !== null && t.balance !== undefined && t.balance !== ""
//         );

//         if (lastTransactionWithBalance) {
//           lastBalance = parseFloat(lastTransactionWithBalance.balance) || 0;
//         }
//       }

//       return lastBalance;
//     } catch (err) {
//       console.error("Error fetching balance:", err);
//       return 0;
//     }
//   },

//   // Create new transaction
//   createTransaction: async (transactionData) => {
//     try {
//       const response = await fetch("https://qwikbistro.shop/transaction/new", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
//         },
//         body: JSON.stringify(transactionData),
//       });
//       return await response.json();
//     } catch (error) {
//       console.error('Error creating transaction:', error);
//       throw error;
//     }
//   },
// };

// const CreatePaymentPage = () => {
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(false);
//   const [userLoading, setUserLoading] = useState(false);
//   const [users, setUsers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [orders, setOrders] = useState([]);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [lastBalance, setLastBalance] = useState(0);

//   // Form state
//   const [formData, setFormData] = useState({
//     date: new Date().toISOString().split("T")[0],
//     customer: null,
//     totalPaid: "",
//     tips: "",
//   });

//   // Fetch all users on component mount
//   useEffect(() => {
//     const fetchUsers = async () => {
//       setUserLoading(true);
//       try {
//         const allUsers = await paymentApiService.searchUsers();
//         setUsers(allUsers);
//       } catch (error) {
//         console.error("Error fetching users:", error);
//         toast.error("Failed to load users");
//       } finally {
//         setUserLoading(false);
//       }
//     };

//     const fetchLastBalance = async () => {
//       const balance = await paymentApiService.getLastBalance();
//       setLastBalance(balance);
//     };

//     fetchUsers();
//     fetchLastBalance();
//   }, []);

//   // Filter users based on search term (client-side filtering)
//   const filteredUsers = useMemo(() => {
//     if (!searchTerm.trim()) return users.slice(0, 20); // Return first 20 users if no search

//     const searchLower = searchTerm.toLowerCase();
//     return users.filter(user =>
//       (user.name && user.name.toLowerCase().includes(searchLower)) ||
//       (user.qb_user_id && user.qb_user_id.toLowerCase().includes(searchLower)) ||
//       (user.phone && user.phone.includes(searchTerm)) ||
//       (user.email && user.email.toLowerCase().includes(searchLower))
//     ).slice(0, 20); // Limit to 20 results
//   }, [users, searchTerm]);

//   // Fetch orders when a user is selected
//   useEffect(() => {
//     const fetchUserOrders = async () => {
//       if (!selectedUser) return;

//       try {
//         const userOrders = await paymentApiService.getUserOrders(selectedUser.id);
//         setOrders(userOrders);
//       } catch (err) {
//         console.error("Error fetching orders:", err);
//         toast.error("Failed to load customer orders");
//       }
//     };

//     fetchUserOrders();
//   }, [selectedUser]);

//   // Handle form input changes
//   const handleInputChange = (field, value) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//   };

//   // Handle user selection
//   const handleUserSelect = (user) => {
//     // setSelectedUser(user);
//     // setFormData(prev => ({ ...prev, customer: user }));
//     // setSelectedOrder(null);


//     setSelectedUser(user);
//     setFormData(prev => ({ ...prev, customer: user }));
//     setSelectedOrder(null);
//     setSearchTerm(''); // Clear search term after selection


//   };

//   // Handle form submission
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!formData.customer || !formData.totalPaid) {
//       toast.error("Please select a customer and enter the payment amount");
//       return;
//     }

//     if (parseFloat(formData.totalPaid) <= 0) {
//       toast.error("Payment amount must be greater than 0");
//       return;
//     }

//     setLoading(true);
//     try {
//       const totalAmount = parseFloat(formData.totalPaid) || 0;
//       const tipsAmount = parseFloat(formData.tips) || 0;
//       const newBalance = (lastBalance + totalAmount + tipsAmount).toFixed(2);

//       const payload = {
//         qb_user_id: formData.customer.qb_user_id,
//         system_user_id: formData.customer.id.toString(),
//         date: formData.date,
//         qb_order_id: selectedOrder ? selectedOrder.qb_order_id : "",
//         system_order_id: selectedOrder ? selectedOrder.id.toString() : "",
//         total: totalAmount.toFixed(2),
//         paid: totalAmount.toFixed(2),
//         tips: tipsAmount.toFixed(2),
//         balance: newBalance,
//       };

//       await paymentApiService.createTransaction(payload);

//       toast.success("Payment processed successfully");

//       // Reset form
//       setFormData({
//         date: new Date().toISOString().split("T")[0],
//         customer: null,
//         totalPaid: "",
//         tips: "",
//       });
//       setSelectedUser(null);
//       setSelectedOrder(null);
//       setSearchTerm("");

//       // Update last balance for next transaction
//       setLastBalance(parseFloat(newBalance));

//       // Navigate after delay
//       // setTimeout(() => {
//       //   navigate("/");
//       // }, 1500);

//     } catch (error) {
//       console.error("Error submitting payment:", error);
//       toast.error("Failed to submit payment");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatCurrency = (amount) => {
//     return `$${parseFloat(amount || 0).toLocaleString('en-US', {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     })}`;
//   };

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Payments" breadcrumbItem="Payment" />

//         <div className="row">
//           <div className="col-12">
//             <div className="card">
//               <div className="card-body">
//                 <h4 className="card-title mb-5 text-center fs-4">Payment</h4>

//                 <form onSubmit={handleSubmit}>
//                   {/* Payment Information */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Date <span className="text-danger">*</span></label>
//                         <input
//                           type="date"
//                           className="form-control"
//                           value={formData.date}
//                           onChange={(e) => handleInputChange('date', e.target.value)}
//                           required
//                         />
//                       </div>
//                     </div>


//                   </div>

//                   {/* Customer Search */}
//                   {/* <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Customer <span className="text-danger">*</span></label>
//                         <div className="position-relative">
//                           <input
//                             type="text"
//                             className="form-control"
//                             value={searchTerm}
//                             onChange={(e) => setSearchTerm(e.target.value)}
//                             placeholder="Search by name, ID, phone, or email..."
//                           />

//                           {searchTerm.length >= 1 && filteredUsers.length > 0 && (
//                             <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-3"
//                               style={{ maxHeight: '250px', overflowY: 'auto' }}>
//                               {filteredUsers.map(user => (
//                                 <div
//                                   key={user.id}
//                                   className={`p-2 border-bottom hover-bg-light cursor-pointer ${selectedUser?.id === user.id ? 'bg-light' : ''}`}
//                                   onClick={() => handleUserSelect(user)}
//                                 >
//                                   <div className="d-flex justify-content-between">
//                                     <span className="fw-medium">{user.name}</span>
//                                     <small className="text-muted">{user.qb_user_id}</small>
//                                   </div>
//                                   <div className="d-flex justify-content-between">
//                                     <small className="text-muted">{user.phone}</small>
//                                     <small className="text-muted">{user.email}</small>
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           )}

//                           {userLoading && (
//                             <div className="position-absolute top-50 end-0 translate-middle-y me-3">
//                               <div className="spinner-border spinner-border-sm text-primary" role="status">
//                                 <span className="visually-hidden">Loading...</span>
//                               </div>
//                             </div>
//                           )}
//                         </div>

//                         {selectedUser && (
//                           <div className="mt-3 p-3 bg-light rounded">
//                             <div className="d-flex align-items-center">
//                               <div className="avatar-sm me-3">
//                                 {selectedUser.photo ? (
//                                   <img
//                                     src={selectedUser.photo}
//                                     alt={selectedUser.name}
//                                     className="rounded-circle img-thumbnail"
//                                     style={{ width: '50px', height: '50px', objectFit: 'cover' }}
//                                   />
//                                 ) : (
//                                   <div className="avatar-sm bg-secondary rounded-circle d-flex align-items-center justify-content-center">
//                                     <i className="fas fa-user text-white"></i>
//                                   </div>
//                                 )}
//                               </div>
//                               <div className="flex-grow-1">
//                                 <h6 className="mb-0">{selectedUser.name}</h6>
//                                 <div className="d-flex gap-3">
//                                   <small className="text-muted">ID: {selectedUser.qb_user_id}</small>
//                                   <small className="text-muted">Phone: {selectedUser.phone}</small>
//                                   {selectedUser.email && (
//                                     <small className="text-muted">Email: {selectedUser.email}</small>
//                                   )}
//                                 </div>
//                               </div>
//                               <button
//                                 type="button"
//                                 className="btn btn-sm btn-light"
//                                 onClick={() => {
//                                   setSelectedUser(null);
//                                   setFormData(prev => ({ ...prev, customer: null }));
//                                   setSearchTerm("");
//                                 }}
//                               >
//                                 <i className="fas fa-times"></i>
//                               </button>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div> */}



//                   {/* Customer Search */}
//                   <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="mb-3">
//                         <label className="form-label">Customer <span className="text-danger">*</span></label>
//                         <div className="position-relative">
//                           <input
//                             type="text"
//                             className="form-control"
//                             value={selectedUser ? `${selectedUser.name} (${selectedUser.qb_user_id})` : searchTerm}
//                             onChange={(e) => {
//                               if (!selectedUser) {
//                                 setSearchTerm(e.target.value);
//                               }
//                             }}
//                             onFocus={() => {
//                               // Clear selection when clicking on input
//                               if (selectedUser) {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }
//                             }}
//                             placeholder={selectedUser ? "Selected customer" : "Search by name, ID, phone, or email..."}
//                           />

//                           {/* Clear selection button when user is selected */}
//                           {selectedUser && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y me-3"
//                               onClick={() => {
//                                 setSelectedUser(null);
//                                 setFormData(prev => ({ ...prev, customer: null }));
//                                 setSearchTerm('');
//                               }}
//                             >
//                               <i className="fas fa-times"></i>
//                             </button>
//                           )}

//                           {/* User search results dropdown */}
//                           {searchTerm.length >= 1 && !selectedUser && filteredUsers.length > 0 && (
//                             <div className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-3"
//                               style={{ maxHeight: '250px', overflowY: 'auto' }}>
//                               {filteredUsers.map(user => (
//                                 <div
//                                   key={user.id}
//                                   className="p-2 border-bottom hover-bg-light cursor-pointer"
//                                   onClick={() => {
//                                     handleUserSelect(user);
//                                     setSearchTerm('');
//                                   }}
//                                 >
//                                   <div className="d-flex justify-content-between">
//                                     <span className="fw-medium">{user.name}</span>
//                                     <small className="text-muted">{user.qb_user_id}</small>
//                                   </div>
//                                   {/* <div className="d-flex justify-content-between">
//                                     <small className="text-muted">{user.phone}</small>
//                                     <small className="text-muted">{user.email}</small>
//                                   </div> */}
//                                 </div>
//                               ))}
//                             </div>
//                           )}

//                           {userLoading && (
//                             <div className="position-absolute top-50 end-0 translate-middle-y me-3">
//                               <div className="spinner-border spinner-border-sm text-primary" role="status">
//                                 <span className="visually-hidden">Loading...</span>
//                               </div>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Payment Amounts */}
//                   <div className="row mb-4">
//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Payment Amount <span className="text-danger">*</span></label>
//                         <div className="input-group">
//                           <span className="input-group-text">$</span>
//                           <input
//                             type="number"
//                             className="form-control"
//                             value={formData.totalPaid}
//                             onChange={(e) => handleInputChange('totalPaid', e.target.value)}
//                             required
//                             step="0.01"
//                             min="0.01"
//                             placeholder="0.00"
//                           />
//                         </div>
//                       </div>
//                     </div>

//                     <div className="col-md-6">
//                       <div className="mb-3">
//                         <label className="form-label">Tips (Optional)</label>
//                         <div className="input-group">
//                           <span className="input-group-text">$</span>
//                           <input
//                             type="number"
//                             className="form-control"
//                             value={formData.tips}
//                             onChange={(e) => handleInputChange('tips', e.target.value)}
//                             step="0.01"
//                             min="0"
//                             placeholder="0.00"
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* New Balance Preview */}
//                   {/* <div className="row mb-4">
//                     <div className="col-md-12">
//                       <div className="card bg-light">
//                         <div className="card-body">
//                           <h6 className="card-title">Balance Summary</h6>
//                           <div className="d-flex justify-content-between">
//                             <span>Current Balance:</span>
//                             <span className="fw-bold">{formatCurrency(lastBalance)}</span>
//                           </div>
//                           <div className="d-flex justify-content-between">
//                             <span>Payment Amount:</span>
//                             <span className="fw-bold text-success">+{formatCurrency(formData.totalPaid)}</span>
//                           </div>
//                           {parseFloat(formData.tips || 0) > 0 && (
//                             <div className="d-flex justify-content-between">
//                               <span>Tips:</span>
//                               <span className="fw-bold text-success">+{formatCurrency(formData.tips)}</span>
//                             </div>
//                           )}
//                           <hr />
//                           <div className="d-flex justify-content-between">
//                             <span className="fw-bold">New Balance:</span>
//                             <span className="fw-bold fs-5">
//                               {formatCurrency(
//                                 lastBalance +
//                                 parseFloat(formData.totalPaid || 0) +
//                                 parseFloat(formData.tips || 0)
//                               )}
//                             </span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div> */}

//                   {/* Form Actions */}
//                   <div className="row">
//                     <div className="col-12">
//                       <div className="d-flex justify-content-between">


//                         <div className="d-flex gap-2">


//                           <button
//                             type="submit"
//                             className="btn btn-success px-4"
//                             disabled={loading || !formData.customer || !formData.totalPaid}
//                           >
//                             {loading ? (
//                               <>
//                                 <i className="fas fa-spinner fa-spin me-1"></i>
//                                 Processing...
//                               </>
//                             ) : (
//                               <>
//                                 <i className="fas fa-dollar-sign me-1"></i>
//                                 Submit Payment
//                               </>
//                             )}
//                           </button>

//                           <button
//                             type="button"
//                             className="btn btn-secondary"
//                             onClick={() => {
//                               setFormData({
//                                 date: new Date().toISOString().split("T")[0],
//                                 customer: null,
//                                 totalPaid: "",
//                                 tips: "",
//                               });
//                               setSelectedUser(null);
//                               setSelectedOrder(null);
//                               setSearchTerm("");
//                             }}
//                             disabled={loading}
//                           >
//                             <i className="fas fa-redo me-1"></i>
//                           </button>

//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>

//       </div>
//     </div>
//   );
// };

// export default CreatePaymentPage;